/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import { CrystalState } from '../types.ts';
import { buildCubicIce } from './iceStructure.ts';
import { CHABAZITE, FAUJASITE, MORDENITE, SILICALITE } from './zeolitePresets.ts';

// Curated starter crystals expressed in P1 — every atom of the conventional
// unit cell is listed explicitly, no symmetry operators required. Lattice
// constants are room-temperature experimental values rounded to four
// decimals (Å) so the round-trip through CIF retains identifiable numbers.

export const NACL: CrystalState = {
  name: 'Sodium Chloride',
  lattice: { a: 5.6402, b: 5.6402, c: 5.6402, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Na', fx: 0, fy: 0, fz: 0, label: 'Na1' },
    { element: 'Na', fx: 0, fy: 0.5, fz: 0.5, label: 'Na2' },
    { element: 'Na', fx: 0.5, fy: 0, fz: 0.5, label: 'Na3' },
    { element: 'Na', fx: 0.5, fy: 0.5, fz: 0, label: 'Na4' },
    { element: 'Cl', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Cl1' },
    { element: 'Cl', fx: 0.5, fy: 0, fz: 0, label: 'Cl2' },
    { element: 'Cl', fx: 0, fy: 0.5, fz: 0, label: 'Cl3' },
    { element: 'Cl', fx: 0, fy: 0, fz: 0.5, label: 'Cl4' },
  ],
  elementCharges: { Na: 1, Cl: -1 },
};

export const DIAMOND: CrystalState = {
  name: 'Diamond',
  lattice: { a: 3.567, b: 3.567, c: 3.567, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F d -3 m',
  basis: [
    { element: 'C', fx: 0, fy: 0, fz: 0, label: 'C1' },
    { element: 'C', fx: 0, fy: 0.5, fz: 0.5, label: 'C2' },
    { element: 'C', fx: 0.5, fy: 0, fz: 0.5, label: 'C3' },
    { element: 'C', fx: 0.5, fy: 0.5, fz: 0, label: 'C4' },
    { element: 'C', fx: 0.25, fy: 0.25, fz: 0.25, label: 'C5' },
    { element: 'C', fx: 0.25, fy: 0.75, fz: 0.75, label: 'C6' },
    { element: 'C', fx: 0.75, fy: 0.25, fz: 0.75, label: 'C7' },
    { element: 'C', fx: 0.75, fy: 0.75, fz: 0.25, label: 'C8' },
  ],
};

export const SILICON: CrystalState = {
  // Same diamond-structure topology as carbon, just a larger lattice
  // constant — useful as a sanity check that scaling works visually.
  name: 'Silicon',
  lattice: { a: 5.431, b: 5.431, c: 5.431, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F d -3 m',
  basis: [
    { element: 'Si', fx: 0, fy: 0, fz: 0, label: 'Si1' },
    { element: 'Si', fx: 0, fy: 0.5, fz: 0.5, label: 'Si2' },
    { element: 'Si', fx: 0.5, fy: 0, fz: 0.5, label: 'Si3' },
    { element: 'Si', fx: 0.5, fy: 0.5, fz: 0, label: 'Si4' },
    { element: 'Si', fx: 0.25, fy: 0.25, fz: 0.25, label: 'Si5' },
    { element: 'Si', fx: 0.25, fy: 0.75, fz: 0.75, label: 'Si6' },
    { element: 'Si', fx: 0.75, fy: 0.25, fz: 0.75, label: 'Si7' },
    { element: 'Si', fx: 0.75, fy: 0.75, fz: 0.25, label: 'Si8' },
  ],
};

export const COPPER_FCC: CrystalState = {
  name: 'Copper',
  lattice: { a: 3.615, b: 3.615, c: 3.615, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Cu', fx: 0, fy: 0, fz: 0, label: 'Cu1' },
    { element: 'Cu', fx: 0, fy: 0.5, fz: 0.5, label: 'Cu2' },
    { element: 'Cu', fx: 0.5, fy: 0, fz: 0.5, label: 'Cu3' },
    { element: 'Cu', fx: 0.5, fy: 0.5, fz: 0, label: 'Cu4' },
  ],
  metallic: true,
};

export const IRON_BCC: CrystalState = {
  name: 'Iron',
  lattice: { a: 2.8665, b: 2.8665, c: 2.8665, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'I m -3 m',
  basis: [
    { element: 'Fe', fx: 0, fy: 0, fz: 0, label: 'Fe1' },
    { element: 'Fe', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Fe2' },
  ],
  metallic: true,
};

export const GRAPHITE: CrystalState = {
  // AB-stacked hexagonal graphite. γ = 120° forces the non-orthogonal cell,
  // a good early test that the triclinic-to-Cartesian conversion handles
  // non-cubic lattices correctly. `layered: true` selects the Tersoff +
  // LJ interlayer force field — pure Tersoff would let the AB sheets
  // dissociate because Tersoff carries no long-range van der Waals.
  name: 'Graphite',
  lattice: { a: 2.461, b: 2.461, c: 6.708, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63/m m c',
  layered: true,
  basis: [
    { element: 'C', fx: 0, fy: 0, fz: 0.25, label: 'C1' },
    { element: 'C', fx: 0, fy: 0, fz: 0.75, label: 'C2' },
    { element: 'C', fx: 1 / 3, fy: 2 / 3, fz: 0.25, label: 'C3' },
    { element: 'C', fx: 2 / 3, fy: 1 / 3, fz: 0.75, label: 'C4' },
  ],
};

// Graphene — a SINGLE hexagonal sheet of sp² carbon, the building block
// of graphite. Same in-plane lattice as graphite (a = 2.461 Å, γ = 120°)
// with a LARGE c-axis (20 Å) acting as vacuum padding so the periodic
// image above never sees the sheet — interlayer LJ cutoff (~4 Å for
// graphite) plus Tersoff cutoff (2.1 Å) are both far less than 20 Å,
// so the kernel sees a true 2D crystal. The sheet sits at z = 0.5·c
// (centered in the cell) for visual clarity.
//
// `layered: false` is the right flag here: single-sheet graphene has
// nothing to interact with via interlayer LJ. Plain Tersoff C handles
// the sp² in-plane physics — the same in-plane physics that runs
// inside each graphite layer. Expect rippling (flexural ZA phonons)
// during MD at finite T; this is real graphene physics, not a bug.
//
// For BILAYER or n-LAYER graphene, copy this preset with multiple
// sheets stacked along c (with c ≈ 3.354 Å · n_layers + vacuum) and
// set `layered: true` to invoke the Tersoff + LJ interlayer FF.
export const GRAPHENE: CrystalState = {
  name: 'Graphene',
  lattice: { a: 2.461, b: 2.461, c: 20.0, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 6/m m m',
  // Atoms on the 2c sites, so the cell origin is a hexagon center — the
  // origin P6/mmm assumes. With an atom at the origin, Fill cell added a
  // third atom at the hexagon center.
  basis: [
    { element: 'C', fx: 1 / 3, fy: 2 / 3, fz: 0.5, label: 'C1' },
    { element: 'C', fx: 2 / 3, fy: 1 / 3, fz: 0.5, label: 'C2' },
  ],
};

// MoS₂ monolayer — the 2D transition-metal dichalcogenide. A single
// S-Mo-S trilayer sandwich: Mo plane at the middle, S atoms above and
// below it. Each Mo sits at the center of a trigonal prism of 6 S
// (3 above, 3 below). In-plane a = 3.16 Å, Mo-S bond = 2.41 Å,
// S-Mo-S angle ≈ 82°. The sandwich is ~3.17 Å thick (top-S to bottom-S).
//
// Large c (20 Å) gives ~16.8 Å of vacuum above and below the trilayer
// so the periodic image along c never interacts. Resolves to the SW
// covalent FF — uses the Kandemir 2016 modified-SW MoS₂ pack with the
// r23 third-leg cutoff that excludes cross-prism S-S pairs (which
// would otherwise have the wrong angle for the single cosθ₀).
//
// Each "monolayer" is geometrically a TRILAYER (S-Mo-S), but it's
// what the field universally calls "single-layer" MoS₂. Bulk 2H-MoS₂
// would stack multiple of these with vdW interlayer cohesion — not
// shipped yet (requires extending the layered FF to also handle SW
// packs, since the current layered FF is Tersoff-only).
//
// Atom positions (h ≈ 1.59 Å = half the trilayer thickness):
//   Mo at z = 0.5·c
//   S above Mo at z = 0.5 + h/c ≈ 0.579
//   S below Mo at z = 0.5 − h/c ≈ 0.421
export const MOS2_MONOLAYER: CrystalState = {
  name: 'MoS2',
  lattice: { a: 3.16, b: 3.16, c: 20.0, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P -6 m 2',
  basis: [
    { element: 'Mo', fx: 1 / 3, fy: 2 / 3, fz: 0.5, label: 'Mo1' },
    { element: 'S', fx: 2 / 3, fy: 1 / 3, fz: 0.5795, label: 'S1' },
    { element: 'S', fx: 2 / 3, fy: 1 / 3, fz: 0.4205, label: 'S2' },
  ],
};

// Bulk 2H-MoS₂ — the natural multi-layer form. P63/mmc, a = 3.160,
// c = 12.295 Å. Two S-Mo-S trilayer sandwiches per unit cell (Mo at
// z = 1/4 and 3/4), AB-stacked so Mo atoms in one layer sit above S
// in the next. Interlayer gap (top-S of one trilayer to bottom-S of
// next) ≈ 2.98 Å; Mo-plane to Mo-plane spacing = c/2 = 6.15 Å.
//
// CAVEAT: this preset resolves to the standard SW covalent FF (no
// layered overlay needed — Kandemir 2016's S-S pair cutoff at 3.82 Å
// already spans the interlayer gap). However, the Kandemir 2016
// parameters were fit for MONOLAYER MoS₂; the SW S-S pair has its
// minimum at ~2.5 Å (well inside the experimental 2.98 Å interlayer
// distance), so during MD the layers will feel a net force trying to
// compress S-S toward 2.5 Å — the bulk structure may distort. For
// quantitative bulk MoS₂ MD, a bulk-targeted FF (ReaxFF, Vashishta,
// or a bulk-fit SW reparametrization) would be required; the
// monolayer preset above is the right place for SW MoS₂ work.
//
// Atom positions match the standard 2H-MoS₂ Wyckoff sites (Mo at 2c,
// S at 4f with z ≈ 0.621). See README in Kandemir's repo for citation
// notes on parameter applicability.
export const MOS2_BULK_2H: CrystalState = {
  name: 'MoS2 (bulk, 2H)',
  lattice: { a: 3.16, b: 3.16, c: 12.295, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63/m m c',
  layered: true,
  basis: [
    // 2H trigonal-prismatic coordination: in each S–Mo–S sandwich the
    // two S atoms sit at the OPPOSITE Wyckoff xy from Mo, so Mo is at
    // the center of a trigonal prism formed by 3 S above and 3 S below
    // (the lateral neighbors come via periodicity). Mo at (1/3, 2/3)
    // ⇒ S at (2/3, 1/3), and vice versa for the AB-shifted upper
    // sandwich. Atoms at the SAME xy would give 1T octahedral, the
    // wrong polytype.
    //
    // Lower sandwich (Mo at z = 1/4 = 0.25):
    { element: 'S', fx: 2 / 3, fy: 1 / 3, fz: 0.121, label: 'S1' }, // bottom of lower sandwich
    { element: 'Mo', fx: 1 / 3, fy: 2 / 3, fz: 0.25, label: 'Mo1' },
    { element: 'S', fx: 2 / 3, fy: 1 / 3, fz: 0.379, label: 'S2' }, // top of lower sandwich
    // Upper sandwich (AB-shifted xy; Mo at z = 3/4 = 0.75):
    { element: 'S', fx: 1 / 3, fy: 2 / 3, fz: 0.621, label: 'S3' }, // bottom of upper sandwich
    { element: 'Mo', fx: 2 / 3, fy: 1 / 3, fz: 0.75, label: 'Mo2' },
    { element: 'S', fx: 1 / 3, fy: 2 / 3, fz: 0.879, label: 'S4' }, // top of upper sandwich
  ],
};

// Bilayer Graphene — TWO graphene sheets in AB (Bernal) stacking,
// the natural 2-layer slice of graphite. Same in-plane lattice as
// graphene/graphite (a = 2.461 Å, γ = 120°), layer spacing 3.354 Å
// (matching graphite's c/2), with vacuum padding above and below the
// bilayer so the periodic image along c never interacts with it.
//
// c = 20 Å gives ~8.3 Å of vacuum on each side of the bilayer (the
// interlayer LJ cutoff is at most ~8 Å, so the bilayer cannot see its
// periodic image). `layered: true` invokes the Tersoff + LJ overlay
// — the LJ binds the two sheets together (without it, plain Tersoff
// would let them dissociate, just as it would for graphite).
//
// AB stacking: the lower sheet has atoms at (0, 0) and (⅓, ⅔); the
// upper sheet at (0, 0) [directly above the lower (0, 0) — "AA"
// alignment] and (⅔, ⅓) [offset from the lower (⅓, ⅔) — "AB" shift].
// Same xy convention as the graphite preset, just collapsed to 2
// sheets surrounded by vacuum.
//
// Expected cohesion ≈ graphene's in-plane (−7.345) + half of graphite's
// interlayer (each sheet has 1 neighbor vs graphite's 2) ≈ −7.37 eV/atom.
export const BILAYER_GRAPHENE: CrystalState = {
  name: 'Bilayer Graphene',
  lattice: { a: 2.461, b: 2.461, c: 20.0, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 3 m 1',
  layered: true,
  basis: [
    // Lower sheet at z = (c − 3.354)/(2c) = 0.4162 — leaves 8.32 Å vacuum below.
    { element: 'C', fx: 0, fy: 0, fz: 0.4162, label: 'C1_A' },
    { element: 'C', fx: 1 / 3, fy: 2 / 3, fz: 0.4162, label: 'C2_A' },
    // Upper sheet at z = (c + 3.354)/(2c) = 0.5838 — leaves 8.32 Å vacuum above.
    // Bernal AB shift: the (0, 0) atom is directly above the lower (0, 0);
    // the second atom is at (⅔, ⅓) — offset from the lower (⅓, ⅔).
    { element: 'C', fx: 0, fy: 0, fz: 0.5838, label: 'C1_B' },
    { element: 'C', fx: 2 / 3, fy: 1 / 3, fz: 0.5838, label: 'C2_B' },
  ],
};

export const SRTIO3_PEROVSKITE: CrystalState = {
  name: 'Strontium Titanate',
  lattice: { a: 3.905, b: 3.905, c: 3.905, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P m -3 m',
  basis: [
    { element: 'Sr', fx: 0, fy: 0, fz: 0, label: 'Sr1' },
    { element: 'Ti', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Ti1' },
    { element: 'O', fx: 0.5, fy: 0.5, fz: 0, label: 'O1' },
    { element: 'O', fx: 0.5, fy: 0, fz: 0.5, label: 'O2' },
    { element: 'O', fx: 0, fy: 0.5, fz: 0.5, label: 'O3' },
  ],
  elementCharges: { Sr: 2, Ti: 4, O: -2 },
};

// Sodium Fluoride — rocksalt, a = 4.62 Å. Smallest of the common alkali
// halides; pairs with LiF / NaCl / KBr for visualizing how the rocksalt
// lattice scales with ion size.
export const NAF_ROCKSALT: CrystalState = {
  name: 'Sodium Fluoride',
  lattice: { a: 4.62, b: 4.62, c: 4.62, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Na', fx: 0, fy: 0, fz: 0, label: 'Na1' },
    { element: 'Na', fx: 0, fy: 0.5, fz: 0.5, label: 'Na2' },
    { element: 'Na', fx: 0.5, fy: 0, fz: 0.5, label: 'Na3' },
    { element: 'Na', fx: 0.5, fy: 0.5, fz: 0, label: 'Na4' },
    { element: 'F', fx: 0.5, fy: 0.5, fz: 0.5, label: 'F1' },
    { element: 'F', fx: 0.5, fy: 0, fz: 0, label: 'F2' },
    { element: 'F', fx: 0, fy: 0.5, fz: 0, label: 'F3' },
    { element: 'F', fx: 0, fy: 0, fz: 0.5, label: 'F4' },
  ],
  elementCharges: { Na: 1, F: -1 },
};

// Potassium Bromide — rocksalt, a = 6.60 Å. Large-ion alkali halide;
// classic IR-transparent window material. Pairs with the smaller-ion
// rocksalt examples (LiF, NaF, NaCl, KCl) to span the series.
export const KBR_ROCKSALT: CrystalState = {
  name: 'Potassium Bromide',
  lattice: { a: 6.6, b: 6.6, c: 6.6, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'K', fx: 0, fy: 0, fz: 0, label: 'K1' },
    { element: 'K', fx: 0, fy: 0.5, fz: 0.5, label: 'K2' },
    { element: 'K', fx: 0.5, fy: 0, fz: 0.5, label: 'K3' },
    { element: 'K', fx: 0.5, fy: 0.5, fz: 0, label: 'K4' },
    { element: 'Br', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Br1' },
    { element: 'Br', fx: 0.5, fy: 0, fz: 0, label: 'Br2' },
    { element: 'Br', fx: 0, fy: 0.5, fz: 0, label: 'Br3' },
    { element: 'Br', fx: 0, fy: 0, fz: 0.5, label: 'Br4' },
  ],
  elementCharges: { K: 1, Br: -1 },
};

// Calcium Oxide — rocksalt with divalent ions (Ca²⁺ / O²⁻),
// a = 4.811 Å. Sister of MgO at one row down in the periodic table;
// useful for showing how the Madelung framework persists across the
// alkaline-earth oxide series (BeO ≠ rocksalt; MgO, CaO, SrO, BaO are).
export const CAO_ROCKSALT: CrystalState = {
  name: 'Calcium Oxide',
  lattice: { a: 4.811, b: 4.811, c: 4.811, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Ca', fx: 0, fy: 0, fz: 0, label: 'Ca1' },
    { element: 'Ca', fx: 0, fy: 0.5, fz: 0.5, label: 'Ca2' },
    { element: 'Ca', fx: 0.5, fy: 0, fz: 0.5, label: 'Ca3' },
    { element: 'Ca', fx: 0.5, fy: 0.5, fz: 0, label: 'Ca4' },
    { element: 'O', fx: 0.5, fy: 0.5, fz: 0.5, label: 'O1' },
    { element: 'O', fx: 0.5, fy: 0, fz: 0, label: 'O2' },
    { element: 'O', fx: 0, fy: 0.5, fz: 0, label: 'O3' },
    { element: 'O', fx: 0, fy: 0, fz: 0.5, label: 'O4' },
  ],
  elementCharges: { Ca: 2, O: -2 },
};

// Lithium Oxide (antifluorite, Li₂O) — inverse of the fluorite structure:
// O²⁻ on the FCC sites, Li⁺ in all 8 tetrahedral interstices. Sister
// structure to fluorite CaF₂ with the cation/anion roles swapped.
// a = 4.619 Å.
export const LI2O_ANTIFLUORITE: CrystalState = {
  name: 'Lithium Oxide',
  lattice: { a: 4.619, b: 4.619, c: 4.619, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'O', fx: 0, fy: 0, fz: 0, label: 'O1' },
    { element: 'O', fx: 0, fy: 0.5, fz: 0.5, label: 'O2' },
    { element: 'O', fx: 0.5, fy: 0, fz: 0.5, label: 'O3' },
    { element: 'O', fx: 0.5, fy: 0.5, fz: 0, label: 'O4' },
    { element: 'Li', fx: 0.25, fy: 0.25, fz: 0.25, label: 'Li1' },
    { element: 'Li', fx: 0.25, fy: 0.75, fz: 0.25, label: 'Li2' },
    { element: 'Li', fx: 0.75, fy: 0.25, fz: 0.25, label: 'Li3' },
    { element: 'Li', fx: 0.75, fy: 0.75, fz: 0.25, label: 'Li4' },
    { element: 'Li', fx: 0.25, fy: 0.25, fz: 0.75, label: 'Li5' },
    { element: 'Li', fx: 0.25, fy: 0.75, fz: 0.75, label: 'Li6' },
    { element: 'Li', fx: 0.75, fy: 0.25, fz: 0.75, label: 'Li7' },
    { element: 'Li', fx: 0.75, fy: 0.75, fz: 0.75, label: 'Li8' },
  ],
  elementCharges: { Li: 1, O: -2 },
};

// Strontium Fluoride — fluorite, a = 5.800 Å. Sister to CaF₂ one row
// down in the alkaline-earth fluoride series (CaF₂, SrF₂, BaF₂).
export const SRF2_FLUORITE: CrystalState = {
  name: 'Strontium Fluoride',
  lattice: { a: 5.8, b: 5.8, c: 5.8, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Sr', fx: 0, fy: 0, fz: 0, label: 'Sr1' },
    { element: 'Sr', fx: 0, fy: 0.5, fz: 0.5, label: 'Sr2' },
    { element: 'Sr', fx: 0.5, fy: 0, fz: 0.5, label: 'Sr3' },
    { element: 'Sr', fx: 0.5, fy: 0.5, fz: 0, label: 'Sr4' },
    { element: 'F', fx: 0.25, fy: 0.25, fz: 0.25, label: 'F1' },
    { element: 'F', fx: 0.25, fy: 0.75, fz: 0.25, label: 'F2' },
    { element: 'F', fx: 0.75, fy: 0.25, fz: 0.25, label: 'F3' },
    { element: 'F', fx: 0.75, fy: 0.75, fz: 0.25, label: 'F4' },
    { element: 'F', fx: 0.25, fy: 0.25, fz: 0.75, label: 'F5' },
    { element: 'F', fx: 0.25, fy: 0.75, fz: 0.75, label: 'F6' },
    { element: 'F', fx: 0.75, fy: 0.25, fz: 0.75, label: 'F7' },
    { element: 'F', fx: 0.75, fy: 0.75, fz: 0.75, label: 'F8' },
  ],
  elementCharges: { Sr: 2, F: -1 },
};

// Barium Titanate (cubic BaTiO₃) — perovskite, Pm-3m, a = 4.006 Å.
// Above ~120 °C BaTiO₃ is cubic paraelectric; below it distorts
// successively to tetragonal / orthorhombic / rhombohedral
// ferroelectric phases. Sister of SrTiO₃ in the ABO₃ perovskite family.
export const BATIO3_PEROVSKITE: CrystalState = {
  name: 'Barium Titanate',
  lattice: { a: 4.006, b: 4.006, c: 4.006, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P m -3 m',
  basis: [
    { element: 'Ba', fx: 0, fy: 0, fz: 0, label: 'Ba1' },
    { element: 'Ti', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Ti1' },
    { element: 'O', fx: 0.5, fy: 0.5, fz: 0, label: 'O1' },
    { element: 'O', fx: 0.5, fy: 0, fz: 0.5, label: 'O2' },
    { element: 'O', fx: 0, fy: 0.5, fz: 0.5, label: 'O3' },
  ],
  elementCharges: { Ba: 2, Ti: 4, O: -2 },
};

// Sodium Iodide — rocksalt, a = 6.473 Å. Largest of the common Na-halide
// series; the doped form NaI(Tl) is the workhorse scintillator for γ-ray
// detection.
export const NAI_ROCKSALT: CrystalState = {
  name: 'Sodium Iodide',
  lattice: { a: 6.473, b: 6.473, c: 6.473, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Na', fx: 0, fy: 0, fz: 0, label: 'Na1' },
    { element: 'Na', fx: 0, fy: 0.5, fz: 0.5, label: 'Na2' },
    { element: 'Na', fx: 0.5, fy: 0, fz: 0.5, label: 'Na3' },
    { element: 'Na', fx: 0.5, fy: 0.5, fz: 0, label: 'Na4' },
    { element: 'I', fx: 0.5, fy: 0.5, fz: 0.5, label: 'I1' },
    { element: 'I', fx: 0.5, fy: 0, fz: 0, label: 'I2' },
    { element: 'I', fx: 0, fy: 0.5, fz: 0, label: 'I3' },
    { element: 'I', fx: 0, fy: 0, fz: 0.5, label: 'I4' },
  ],
  elementCharges: { Na: 1, I: -1 },
};

// Strontium Oxide — rocksalt, a = 5.16 Å. Mg → Ca → Sr step in the
// alkaline-earth oxide series.
export const SRO_ROCKSALT: CrystalState = {
  name: 'Strontium Oxide',
  lattice: { a: 5.16, b: 5.16, c: 5.16, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Sr', fx: 0, fy: 0, fz: 0, label: 'Sr1' },
    { element: 'Sr', fx: 0, fy: 0.5, fz: 0.5, label: 'Sr2' },
    { element: 'Sr', fx: 0.5, fy: 0, fz: 0.5, label: 'Sr3' },
    { element: 'Sr', fx: 0.5, fy: 0.5, fz: 0, label: 'Sr4' },
    { element: 'O', fx: 0.5, fy: 0.5, fz: 0.5, label: 'O1' },
    { element: 'O', fx: 0.5, fy: 0, fz: 0, label: 'O2' },
    { element: 'O', fx: 0, fy: 0.5, fz: 0, label: 'O3' },
    { element: 'O', fx: 0, fy: 0, fz: 0.5, label: 'O4' },
  ],
  elementCharges: { Sr: 2, O: -2 },
};

// Barium Fluoride — fluorite, a = 6.200 Å. CaF₂ → SrF₂ → BaF₂ alkaline-
// earth fluoride series; commonly used as an IR-transparent optical
// material and as a fast scintillator.
export const BAF2_FLUORITE: CrystalState = {
  name: 'Barium Fluoride',
  lattice: { a: 6.2, b: 6.2, c: 6.2, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Ba', fx: 0, fy: 0, fz: 0, label: 'Ba1' },
    { element: 'Ba', fx: 0, fy: 0.5, fz: 0.5, label: 'Ba2' },
    { element: 'Ba', fx: 0.5, fy: 0, fz: 0.5, label: 'Ba3' },
    { element: 'Ba', fx: 0.5, fy: 0.5, fz: 0, label: 'Ba4' },
    { element: 'F', fx: 0.25, fy: 0.25, fz: 0.25, label: 'F1' },
    { element: 'F', fx: 0.25, fy: 0.75, fz: 0.25, label: 'F2' },
    { element: 'F', fx: 0.75, fy: 0.25, fz: 0.25, label: 'F3' },
    { element: 'F', fx: 0.75, fy: 0.75, fz: 0.25, label: 'F4' },
    { element: 'F', fx: 0.25, fy: 0.25, fz: 0.75, label: 'F5' },
    { element: 'F', fx: 0.25, fy: 0.75, fz: 0.75, label: 'F6' },
    { element: 'F', fx: 0.75, fy: 0.25, fz: 0.75, label: 'F7' },
    { element: 'F', fx: 0.75, fy: 0.75, fz: 0.75, label: 'F8' },
  ],
  elementCharges: { Ba: 2, F: -1 },
};

// Magnesium Fluoride — rutile structure, a = 4.625, c = 3.052 Å. Same
// P 42/m n m topology as TiO₂ rutile but with Mg²⁺ and F⁻ replacing
// Ti⁴⁺ and O²⁻. Optical material for UV / IR windows.
export const MGF2_RUTILE: CrystalState = {
  name: 'Magnesium Fluoride',
  lattice: { a: 4.625, b: 4.625, c: 3.052, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P 42/m n m',
  basis: [
    { element: 'Mg', fx: 0, fy: 0, fz: 0, label: 'Mg1' },
    { element: 'Mg', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Mg2' },
    { element: 'F', fx: 0.303, fy: 0.303, fz: 0, label: 'F1' },
    { element: 'F', fx: 0.697, fy: 0.697, fz: 0, label: 'F2' },
    { element: 'F', fx: 0.803, fy: 0.197, fz: 0.5, label: 'F3' },
    { element: 'F', fx: 0.197, fy: 0.803, fz: 0.5, label: 'F4' },
  ],
  elementCharges: { Mg: 2, F: -1 },
};

// Tin Dioxide (cassiterite) — rutile structure, a = 4.738, c = 3.187 Å.
// Wide-bandgap transparent conductor (n-type), the parent of many doped
// transparent-conducting oxides (ITO-like systems). Same rutile topology
// as TiO₂ / MgF₂.
export const SNO2_RUTILE: CrystalState = {
  name: 'Tin Dioxide',
  lattice: { a: 4.738, b: 4.738, c: 3.187, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P 42/m n m',
  basis: [
    { element: 'Sn', fx: 0, fy: 0, fz: 0, label: 'Sn1' },
    { element: 'Sn', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Sn2' },
    { element: 'O', fx: 0.307, fy: 0.307, fz: 0, label: 'O1' },
    { element: 'O', fx: 0.693, fy: 0.693, fz: 0, label: 'O2' },
    { element: 'O', fx: 0.807, fy: 0.193, fz: 0.5, label: 'O3' },
    { element: 'O', fx: 0.193, fy: 0.807, fz: 0.5, label: 'O4' },
  ],
  elementCharges: { Sn: 4, O: -2 },
};

// Calcium Titanate (CaTiO₃) — the namesake perovskite mineral. Cubic
// Pm-3m above ~1380 K, a = 3.84 Å; at room temperature it adopts an
// orthorhombic Pnma distortion, but the cubic high-T aristotype is the
// pedagogically clean form. Ca at the corner, Ti at the body center, O
// at the face centers — Ti-O₆ corner-sharing octahedra with Ca filling
// the cuboctahedral A-site.
export const CATIO3_PEROVSKITE: CrystalState = {
  name: 'Calcium Titanate',
  lattice: { a: 3.84, b: 3.84, c: 3.84, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P m -3 m',
  basis: [
    { element: 'Ca', fx: 0, fy: 0, fz: 0, label: 'Ca1' },
    { element: 'Ti', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Ti1' },
    { element: 'O', fx: 0.5, fy: 0.5, fz: 0, label: 'O1' },
    { element: 'O', fx: 0.5, fy: 0, fz: 0.5, label: 'O2' },
    { element: 'O', fx: 0, fy: 0.5, fz: 0.5, label: 'O3' },
  ],
  elementCharges: { Ca: 2, Ti: 4, O: -2 },
};

// CsCl-type structure — simple cubic alternative to rock salt. Each ion sits
// at the body center of the other's primitive cube. Counterpoint to NaCl
// (FCC sublattices) for visualizing how ion-size ratios drive packing.
export const CSCL: CrystalState = {
  name: 'Cesium Chloride',
  lattice: { a: 4.123, b: 4.123, c: 4.123, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P m -3 m',
  basis: [
    { element: 'Cs', fx: 0, fy: 0, fz: 0, label: 'Cs1' },
    { element: 'Cl', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Cl1' },
  ],
  elementCharges: { Cs: 1, Cl: -1 },
};

// Fluorite structure — 1:2 ionic. Ca²⁺ on FCC sites, F⁻ filling every
// tetrahedral interstice. Antifluorite (Li₂O) has the inverse arrangement;
// keep this as the canonical fluorite example.
export const FLUORITE: CrystalState = {
  name: 'Calcium Fluoride',
  lattice: { a: 5.4631, b: 5.4631, c: 5.4631, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Ca', fx: 0, fy: 0, fz: 0, label: 'Ca1' },
    { element: 'Ca', fx: 0, fy: 0.5, fz: 0.5, label: 'Ca2' },
    { element: 'Ca', fx: 0.5, fy: 0, fz: 0.5, label: 'Ca3' },
    { element: 'Ca', fx: 0.5, fy: 0.5, fz: 0, label: 'Ca4' },
    { element: 'F', fx: 0.25, fy: 0.25, fz: 0.25, label: 'F1' },
    { element: 'F', fx: 0.25, fy: 0.75, fz: 0.25, label: 'F2' },
    { element: 'F', fx: 0.75, fy: 0.25, fz: 0.25, label: 'F3' },
    { element: 'F', fx: 0.75, fy: 0.75, fz: 0.25, label: 'F4' },
    { element: 'F', fx: 0.25, fy: 0.25, fz: 0.75, label: 'F5' },
    { element: 'F', fx: 0.25, fy: 0.75, fz: 0.75, label: 'F6' },
    { element: 'F', fx: 0.75, fy: 0.25, fz: 0.75, label: 'F7' },
    { element: 'F', fx: 0.75, fy: 0.75, fz: 0.75, label: 'F8' },
  ],
  elementCharges: { Ca: 2, F: -1 },
};

// Zincblende (sphalerite) — diamond topology with two interpenetrating FCC
// sublattices, one Ga and one As. Canonical III-V semiconductor; the same
// structure type covers ZnS, InP, AlAs, etc.
export const GAAS_ZINCBLENDE: CrystalState = {
  name: 'Gallium Arsenide',
  lattice: { a: 5.6535, b: 5.6535, c: 5.6535, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Ga', fx: 0, fy: 0, fz: 0, label: 'Ga1' },
    { element: 'Ga', fx: 0, fy: 0.5, fz: 0.5, label: 'Ga2' },
    { element: 'Ga', fx: 0.5, fy: 0, fz: 0.5, label: 'Ga3' },
    { element: 'Ga', fx: 0.5, fy: 0.5, fz: 0, label: 'Ga4' },
    { element: 'As', fx: 0.25, fy: 0.25, fz: 0.25, label: 'As1' },
    { element: 'As', fx: 0.25, fy: 0.75, fz: 0.75, label: 'As2' },
    { element: 'As', fx: 0.75, fy: 0.25, fz: 0.75, label: 'As3' },
    { element: 'As', fx: 0.75, fy: 0.75, fz: 0.25, label: 'As4' },
  ],
};

// Wurtzite ZnO — hexagonal analog of zincblende. ZnS, GaN, AlN, SiC all
// share this structure. The "u" parameter for the O sublattice is ~0.382
// for ZnO (ideal wurtzite would be 0.375 = 3/8). Treated as ionic for the
// ESP-coloring path.
export const ZNO_WURTZITE: CrystalState = {
  name: 'Zinc Oxide',
  lattice: { a: 3.25, b: 3.25, c: 5.207, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63 m c',
  basis: [
    { element: 'Zn', fx: 1 / 3, fy: 2 / 3, fz: 0, label: 'Zn1' },
    { element: 'Zn', fx: 2 / 3, fy: 1 / 3, fz: 0.5, label: 'Zn2' },
    { element: 'O', fx: 1 / 3, fy: 2 / 3, fz: 0.382, label: 'O1' },
    { element: 'O', fx: 2 / 3, fy: 1 / 3, fz: 0.882, label: 'O2' },
  ],
  elementCharges: { Zn: 2, O: -2 },
};

// Rutile TiO₂ — tetragonal (a = b ≠ c). Ti at the corners and body center
// with octahedral O coordination. Internal "u" parameter ≈ 0.305 for the
// O positions in the basal plane.
export const RUTILE_TIO2: CrystalState = {
  name: 'Rutile',
  lattice: { a: 4.594, b: 4.594, c: 2.959, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P 42/m n m',
  basis: [
    { element: 'Ti', fx: 0, fy: 0, fz: 0, label: 'Ti1' },
    { element: 'Ti', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Ti2' },
    { element: 'O', fx: 0.305, fy: 0.305, fz: 0, label: 'O1' },
    { element: 'O', fx: 0.695, fy: 0.695, fz: 0, label: 'O2' },
    { element: 'O', fx: 0.805, fy: 0.195, fz: 0.5, label: 'O3' },
    { element: 'O', fx: 0.195, fy: 0.805, fz: 0.5, label: 'O4' },
  ],
  elementCharges: { Ti: 4, O: -2 },
};

// Magnesium HCP — hexagonal close-packed metal, the third basic metal
// packing alongside FCC (Cu) and BCC (Fe). c/a ≈ 1.624, close to the
// ideal-sphere-packing value 1.633. Same convention as Cu/Fe: metallic
// flag set so no bonds are drawn.
export const MAGNESIUM_HCP: CrystalState = {
  name: 'Magnesium',
  lattice: { a: 3.21, b: 3.21, c: 5.21, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63/m m c',
  basis: [
    { element: 'Mg', fx: 1 / 3, fy: 2 / 3, fz: 0.25, label: 'Mg1' },
    { element: 'Mg', fx: 2 / 3, fy: 1 / 3, fz: 0.75, label: 'Mg2' },
  ],
  metallic: true,
  conventionalCell: 'hexagonal-prism',
};

// MgO (periclase) — divalent ionic rocksalt. Same Fm-3m structure as NaCl
// but with Mg²⁺ / O²⁻ instead of monovalent Na⁺ / Cl⁻. Useful for showing
// how stronger ionic charges affect ESP-surface coloring at the same
// topology.
export const MGO_ROCKSALT: CrystalState = {
  name: 'Magnesium Oxide',
  lattice: { a: 4.2117, b: 4.2117, c: 4.2117, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Mg', fx: 0, fy: 0, fz: 0, label: 'Mg1' },
    { element: 'Mg', fx: 0, fy: 0.5, fz: 0.5, label: 'Mg2' },
    { element: 'Mg', fx: 0.5, fy: 0, fz: 0.5, label: 'Mg3' },
    { element: 'Mg', fx: 0.5, fy: 0.5, fz: 0, label: 'Mg4' },
    { element: 'O', fx: 0.5, fy: 0.5, fz: 0.5, label: 'O1' },
    { element: 'O', fx: 0.5, fy: 0, fz: 0, label: 'O2' },
    { element: 'O', fx: 0, fy: 0.5, fz: 0, label: 'O3' },
    { element: 'O', fx: 0, fy: 0, fz: 0.5, label: 'O4' },
  ],
  elementCharges: { Mg: 2, O: -2 },
};

// Hexagonal boron nitride — "white graphite". Same P6_3/mmc layered
// structure as graphite, but B and N alternate within each layer, and the
// AA' stacking places B directly above N (vs graphite's AB carbon-on-
// hexagon-center). The wide-bandgap 2D analog of graphite/graphene.
// `layered: true` invokes the Tersoff + LJ interlayer FF, with the
// Karaaslan 2020 BN Tersoff in-plane and UFF B/N parameters for the LJ
// interlayer pass — pure Tersoff alone (the c-BN-applicable fit) would
// let the sheets dissociate, just like graphite.
export const HBN_HEXAGONAL: CrystalState = {
  name: 'Hexagonal Boron Nitride',
  lattice: { a: 2.504, b: 2.504, c: 6.661, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63/m m c',
  layered: true,
  basis: [
    { element: 'B', fx: 1 / 3, fy: 2 / 3, fz: 0.25, label: 'B1' },
    { element: 'B', fx: 2 / 3, fy: 1 / 3, fz: 0.75, label: 'B2' },
    { element: 'N', fx: 2 / 3, fy: 1 / 3, fz: 0.25, label: 'N1' },
    { element: 'N', fx: 1 / 3, fy: 2 / 3, fz: 0.75, label: 'N2' },
  ],
};

// Cubic boron nitride — diamond analog with B and N. Zincblende-type
// structure (F-43m), nearly as hard as diamond. Counterpart to h-BN at
// high pressure, like diamond is to graphite.
export const CBN_CUBIC: CrystalState = {
  name: 'Cubic Boron Nitride',
  lattice: { a: 3.615, b: 3.615, c: 3.615, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'B', fx: 0, fy: 0, fz: 0, label: 'B1' },
    { element: 'B', fx: 0, fy: 0.5, fz: 0.5, label: 'B2' },
    { element: 'B', fx: 0.5, fy: 0, fz: 0.5, label: 'B3' },
    { element: 'B', fx: 0.5, fy: 0.5, fz: 0, label: 'B4' },
    { element: 'N', fx: 0.25, fy: 0.25, fz: 0.25, label: 'N1' },
    { element: 'N', fx: 0.25, fy: 0.75, fz: 0.75, label: 'N2' },
    { element: 'N', fx: 0.75, fy: 0.25, fz: 0.75, label: 'N3' },
    { element: 'N', fx: 0.75, fy: 0.75, fz: 0.25, label: 'N4' },
  ],
};

// Gallium Phosphide (GaP) — III-V zincblende semiconductor, indirect
// bandgap, a = 5.4505 Å. Same F-43m topology as GaAs.
export const GAP_ZINCBLENDE: CrystalState = {
  name: 'Gallium Phosphide',
  lattice: { a: 5.4505, b: 5.4505, c: 5.4505, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Ga', fx: 0, fy: 0, fz: 0, label: 'Ga1' },
    { element: 'Ga', fx: 0, fy: 0.5, fz: 0.5, label: 'Ga2' },
    { element: 'Ga', fx: 0.5, fy: 0, fz: 0.5, label: 'Ga3' },
    { element: 'Ga', fx: 0.5, fy: 0.5, fz: 0, label: 'Ga4' },
    { element: 'P', fx: 0.25, fy: 0.25, fz: 0.25, label: 'P1' },
    { element: 'P', fx: 0.25, fy: 0.75, fz: 0.75, label: 'P2' },
    { element: 'P', fx: 0.75, fy: 0.25, fz: 0.75, label: 'P3' },
    { element: 'P', fx: 0.75, fy: 0.75, fz: 0.25, label: 'P4' },
  ],
};

// Indium Phosphide (InP) — III-V zincblende, direct bandgap workhorse
// for telecom lasers and photodetectors. a = 5.8687 Å.
export const INP_ZINCBLENDE: CrystalState = {
  name: 'Indium Phosphide',
  lattice: { a: 5.8687, b: 5.8687, c: 5.8687, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'In', fx: 0, fy: 0, fz: 0, label: 'In1' },
    { element: 'In', fx: 0, fy: 0.5, fz: 0.5, label: 'In2' },
    { element: 'In', fx: 0.5, fy: 0, fz: 0.5, label: 'In3' },
    { element: 'In', fx: 0.5, fy: 0.5, fz: 0, label: 'In4' },
    { element: 'P', fx: 0.25, fy: 0.25, fz: 0.25, label: 'P1' },
    { element: 'P', fx: 0.25, fy: 0.75, fz: 0.75, label: 'P2' },
    { element: 'P', fx: 0.75, fy: 0.25, fz: 0.75, label: 'P3' },
    { element: 'P', fx: 0.75, fy: 0.75, fz: 0.25, label: 'P4' },
  ],
};

// Indium Arsenide (InAs) — III-V zincblende, narrow direct bandgap
// (~0.36 eV); widely used for mid-IR detectors and high-mobility FETs.
// a = 6.0583 Å.
export const INAS_ZINCBLENDE: CrystalState = {
  name: 'Indium Arsenide',
  lattice: { a: 6.0583, b: 6.0583, c: 6.0583, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'In', fx: 0, fy: 0, fz: 0, label: 'In1' },
    { element: 'In', fx: 0, fy: 0.5, fz: 0.5, label: 'In2' },
    { element: 'In', fx: 0.5, fy: 0, fz: 0.5, label: 'In3' },
    { element: 'In', fx: 0.5, fy: 0.5, fz: 0, label: 'In4' },
    { element: 'As', fx: 0.25, fy: 0.25, fz: 0.25, label: 'As1' },
    { element: 'As', fx: 0.25, fy: 0.75, fz: 0.75, label: 'As2' },
    { element: 'As', fx: 0.75, fy: 0.25, fz: 0.75, label: 'As3' },
    { element: 'As', fx: 0.75, fy: 0.75, fz: 0.25, label: 'As4' },
  ],
};

// Cadmium Telluride (CdTe) — II-VI zincblende, direct bandgap
// (~1.45 eV) used in thin-film solar cells. a = 6.482 Å.
export const CDTE_ZINCBLENDE: CrystalState = {
  name: 'Cadmium Telluride',
  lattice: { a: 6.482, b: 6.482, c: 6.482, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Cd', fx: 0, fy: 0, fz: 0, label: 'Cd1' },
    { element: 'Cd', fx: 0, fy: 0.5, fz: 0.5, label: 'Cd2' },
    { element: 'Cd', fx: 0.5, fy: 0, fz: 0.5, label: 'Cd3' },
    { element: 'Cd', fx: 0.5, fy: 0.5, fz: 0, label: 'Cd4' },
    { element: 'Te', fx: 0.25, fy: 0.25, fz: 0.25, label: 'Te1' },
    { element: 'Te', fx: 0.25, fy: 0.75, fz: 0.75, label: 'Te2' },
    { element: 'Te', fx: 0.75, fy: 0.25, fz: 0.75, label: 'Te3' },
    { element: 'Te', fx: 0.75, fy: 0.75, fz: 0.25, label: 'Te4' },
  ],
};

// Lonsdaleite — hexagonal diamond (P6_3/mmc), the wurtzite-type
// polymorph of diamond. C-C ≈ 1.54 Å as in cubic diamond, but with AB
// stacking instead of ABC. Found in shocked meteorites; pedagogically
// the hex sibling of diamond just as wurtzite ZnO is the hex sibling
// of zincblende ZnS. a = 2.522, c = 4.119 Å (ideal c/a ≈ 1.633).
export const LONSDALEITE: CrystalState = {
  name: 'Lonsdaleite',
  lattice: { a: 2.522, b: 2.522, c: 4.119, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63/m m c',
  // The 4f sites (⅓,⅔,z) with z = 1/16, in the origin P6₃/mmc assumes. The
  // same structure written from z = 0 made Fill cell double every atom.
  basis: [
    { element: 'C', fx: 1 / 3, fy: 2 / 3, fz: 1 / 16, label: 'C1' },
    { element: 'C', fx: 2 / 3, fy: 1 / 3, fz: 9 / 16, label: 'C2' },
    { element: 'C', fx: 1 / 3, fy: 2 / 3, fz: 7 / 16, label: 'C3' },
    { element: 'C', fx: 2 / 3, fy: 1 / 3, fz: 15 / 16, label: 'C4' },
  ],
};

// Aluminum Phosphide (AlP) — III-V zincblende, indirect bandgap.
// a = 5.4510 Å. Same F-43m topology as GaP / GaAs.
export const ALP_ZINCBLENDE: CrystalState = {
  name: 'Aluminum Phosphide',
  lattice: { a: 5.451, b: 5.451, c: 5.451, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Al', fx: 0, fy: 0, fz: 0, label: 'Al1' },
    { element: 'Al', fx: 0, fy: 0.5, fz: 0.5, label: 'Al2' },
    { element: 'Al', fx: 0.5, fy: 0, fz: 0.5, label: 'Al3' },
    { element: 'Al', fx: 0.5, fy: 0.5, fz: 0, label: 'Al4' },
    { element: 'P', fx: 0.25, fy: 0.25, fz: 0.25, label: 'P1' },
    { element: 'P', fx: 0.25, fy: 0.75, fz: 0.75, label: 'P2' },
    { element: 'P', fx: 0.75, fy: 0.25, fz: 0.75, label: 'P3' },
    { element: 'P', fx: 0.75, fy: 0.75, fz: 0.25, label: 'P4' },
  ],
};

// Indium Antimonide (InSb) — III-V zincblende with the narrowest bandgap
// of the common III-V series (~0.17 eV); IR detectors and Hall sensors.
// a = 6.4794 Å.
export const INSB_ZINCBLENDE: CrystalState = {
  name: 'Indium Antimonide',
  lattice: { a: 6.4794, b: 6.4794, c: 6.4794, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'In', fx: 0, fy: 0, fz: 0, label: 'In1' },
    { element: 'In', fx: 0, fy: 0.5, fz: 0.5, label: 'In2' },
    { element: 'In', fx: 0.5, fy: 0, fz: 0.5, label: 'In3' },
    { element: 'In', fx: 0.5, fy: 0.5, fz: 0, label: 'In4' },
    { element: 'Sb', fx: 0.25, fy: 0.25, fz: 0.25, label: 'Sb1' },
    { element: 'Sb', fx: 0.25, fy: 0.75, fz: 0.75, label: 'Sb2' },
    { element: 'Sb', fx: 0.75, fy: 0.25, fz: 0.75, label: 'Sb3' },
    { element: 'Sb', fx: 0.75, fy: 0.75, fz: 0.25, label: 'Sb4' },
  ],
};

// Zinc Selenide (ZnSe) — II-VI zincblende, direct bandgap (~2.7 eV);
// blue/UV optoelectronics. a = 5.6692 Å.
export const ZNSE_ZINCBLENDE: CrystalState = {
  name: 'Zinc Selenide',
  lattice: { a: 5.6692, b: 5.6692, c: 5.6692, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Zn', fx: 0, fy: 0, fz: 0, label: 'Zn1' },
    { element: 'Zn', fx: 0, fy: 0.5, fz: 0.5, label: 'Zn2' },
    { element: 'Zn', fx: 0.5, fy: 0, fz: 0.5, label: 'Zn3' },
    { element: 'Zn', fx: 0.5, fy: 0.5, fz: 0, label: 'Zn4' },
    { element: 'Se', fx: 0.25, fy: 0.25, fz: 0.25, label: 'Se1' },
    { element: 'Se', fx: 0.25, fy: 0.75, fz: 0.75, label: 'Se2' },
    { element: 'Se', fx: 0.75, fy: 0.25, fz: 0.75, label: 'Se3' },
    { element: 'Se', fx: 0.75, fy: 0.75, fz: 0.25, label: 'Se4' },
  ],
};

// α-Tin (gray tin) — diamond-structure semimetal, the group-14 cousin
// of Si and Ge below room temperature. Above ~13 °C it converts to
// metallic β-Sn (tetragonal). a = 6.4892 Å, Sn-Sn = 2.81 Å.
export const ALPHA_TIN: CrystalState = {
  name: 'Gray Tin',
  lattice: { a: 6.4892, b: 6.4892, c: 6.4892, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F d -3 m',
  basis: [
    { element: 'Sn', fx: 0, fy: 0, fz: 0, label: 'Sn1' },
    { element: 'Sn', fx: 0, fy: 0.5, fz: 0.5, label: 'Sn2' },
    { element: 'Sn', fx: 0.5, fy: 0, fz: 0.5, label: 'Sn3' },
    { element: 'Sn', fx: 0.5, fy: 0.5, fz: 0, label: 'Sn4' },
    { element: 'Sn', fx: 0.25, fy: 0.25, fz: 0.25, label: 'Sn5' },
    { element: 'Sn', fx: 0.25, fy: 0.75, fz: 0.75, label: 'Sn6' },
    { element: 'Sn', fx: 0.75, fy: 0.25, fz: 0.75, label: 'Sn7' },
    { element: 'Sn', fx: 0.75, fy: 0.75, fz: 0.25, label: 'Sn8' },
  ],
};

// β-Cristobalite — high-T cubic polymorph of silica (SiO₂), Fd-3m,
// a = 7.16 Å. Si atoms form the diamond sublattice; O atoms sit at the
// midpoints of Si-Si bonds, giving a 3D corner-sharing tetrahedral
// network. Si-O ≈ 1.55 Å. Real β-cristobalite has a slight off-axis
// O displacement (Fd-3m → I-42d in disordered models); idealized here
// with O exactly at bond midpoints.
export const BETA_CRISTOBALITE: CrystalState = {
  name: 'Cristobalite',
  lattice: { a: 7.16, b: 7.16, c: 7.16, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F d -3 m',
  basis: [
    // Si at 8a (diamond positions)
    { element: 'Si', fx: 0, fy: 0, fz: 0, label: 'Si1' },
    { element: 'Si', fx: 0, fy: 0.5, fz: 0.5, label: 'Si2' },
    { element: 'Si', fx: 0.5, fy: 0, fz: 0.5, label: 'Si3' },
    { element: 'Si', fx: 0.5, fy: 0.5, fz: 0, label: 'Si4' },
    { element: 'Si', fx: 0.25, fy: 0.25, fz: 0.25, label: 'Si5' },
    { element: 'Si', fx: 0.25, fy: 0.75, fz: 0.75, label: 'Si6' },
    { element: 'Si', fx: 0.75, fy: 0.25, fz: 0.75, label: 'Si7' },
    { element: 'Si', fx: 0.75, fy: 0.75, fz: 0.25, label: 'Si8' },
    // O at midpoints of Si-Si bonds (16 unique positions)
    { element: 'O', fx: 0.125, fy: 0.125, fz: 0.125, label: 'O1' },
    { element: 'O', fx: 0.125, fy: 0.875, fz: 0.875, label: 'O2' },
    { element: 'O', fx: 0.875, fy: 0.125, fz: 0.875, label: 'O3' },
    { element: 'O', fx: 0.875, fy: 0.875, fz: 0.125, label: 'O4' },
    { element: 'O', fx: 0.125, fy: 0.625, fz: 0.625, label: 'O5' },
    { element: 'O', fx: 0.125, fy: 0.375, fz: 0.375, label: 'O6' },
    { element: 'O', fx: 0.875, fy: 0.625, fz: 0.375, label: 'O7' },
    { element: 'O', fx: 0.875, fy: 0.375, fz: 0.625, label: 'O8' },
    { element: 'O', fx: 0.625, fy: 0.125, fz: 0.625, label: 'O9' },
    { element: 'O', fx: 0.375, fy: 0.125, fz: 0.375, label: 'O10' },
    { element: 'O', fx: 0.625, fy: 0.875, fz: 0.375, label: 'O11' },
    { element: 'O', fx: 0.375, fy: 0.875, fz: 0.625, label: 'O12' },
    { element: 'O', fx: 0.625, fy: 0.625, fz: 0.125, label: 'O13' },
    { element: 'O', fx: 0.375, fy: 0.375, fz: 0.125, label: 'O14' },
    { element: 'O', fx: 0.625, fy: 0.375, fz: 0.875, label: 'O15' },
    { element: 'O', fx: 0.375, fy: 0.625, fz: 0.875, label: 'O16' },
  ],
};

// All-silica sodalite (clathrasil, SOD framework) — the canonical
// pedagogical zeolite. Tetrahedrally connected SiO4 framework forming
// truncated-octahedral β-cages; the smallest zeolite framework type and
// the building block of larger zeolites (LTA, FAU).
//
// Geometry: Im-3m, a = 8.965 Å (Bibby & Dale 1985 synthesis). Si on the
// 12d Wyckoff site, O on the 24h site with x = 0.149 in the (x, x, 1/2)
// parameterization. Gives Si–O = 1.61 Å (right at the BKS equilibrium)
// and Si–O–Si = 158° (BKS preferred ≈ 155°), so an NVE start sits
// almost exactly at the BKS minimum and doesn't shed lattice strain
// into kinetic energy.
//
// NOT to be confused with natural sodalite, Na8Cl2[AlSiO4]6, which has
// alternating Al/Si (Löwenstein rule → P-43n) plus Na+ and Cl- cage
// guests. Replacing those T-sites with Si naively keeps the P-43n
// asymmetry, leaving half the bonds at the Al-O length (~1.72 Å) — way
// outside BKS Si-O equilibrium, which is exactly the failure mode that
// caused the first revision of this preset to heat to 10000 K under NVE.
//
// Categorized as covalent (no elementCharges) — the BKS force field
// applies automatically because the 'O,Si' composition key matches.
export const SODALITE: CrystalState = {
  name: 'Sodalite',
  lattice: { a: 8.965, b: 8.965, c: 8.965, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'I m -3 m',
  basis: [
    // Si at 12d
    { element: 'Si', fx: 0.25, fy: 0, fz: 0.5, label: 'Si1' },
    { element: 'Si', fx: 0.75, fy: 0, fz: 0.5, label: 'Si2' },
    { element: 'Si', fx: 0.5, fy: 0.25, fz: 0, label: 'Si3' },
    { element: 'Si', fx: 0.5, fy: 0.75, fz: 0, label: 'Si4' },
    { element: 'Si', fx: 0, fy: 0.5, fz: 0.25, label: 'Si5' },
    { element: 'Si', fx: 0, fy: 0.5, fz: 0.75, label: 'Si6' },
    { element: 'Si', fx: 0.25, fy: 0.5, fz: 0, label: 'Si7' },
    { element: 'Si', fx: 0.75, fy: 0.5, fz: 0, label: 'Si8' },
    { element: 'Si', fx: 0, fy: 0.25, fz: 0.5, label: 'Si9' },
    { element: 'Si', fx: 0, fy: 0.75, fz: 0.5, label: 'Si10' },
    { element: 'Si', fx: 0.5, fy: 0, fz: 0.25, label: 'Si11' },
    { element: 'Si', fx: 0.5, fy: 0, fz: 0.75, label: 'Si12' },
    // O at 24h with x = 0.149 — full Im-3m orbit, 24 atoms.
    // Each O bridges two Si atoms at 1.61 Å with Si-O-Si = 158°.
    { element: 'O', fx: 0.149, fy: 0.149, fz: 0.5, label: 'O1' },
    { element: 'O', fx: 0.851, fy: 0.851, fz: 0.5, label: 'O2' },
    { element: 'O', fx: 0.149, fy: 0.851, fz: 0.5, label: 'O3' },
    { element: 'O', fx: 0.851, fy: 0.149, fz: 0.5, label: 'O4' },
    { element: 'O', fx: 0.5, fy: 0.149, fz: 0.149, label: 'O5' },
    { element: 'O', fx: 0.5, fy: 0.851, fz: 0.851, label: 'O6' },
    { element: 'O', fx: 0.5, fy: 0.149, fz: 0.851, label: 'O7' },
    { element: 'O', fx: 0.5, fy: 0.851, fz: 0.149, label: 'O8' },
    { element: 'O', fx: 0.149, fy: 0.5, fz: 0.149, label: 'O9' },
    { element: 'O', fx: 0.851, fy: 0.5, fz: 0.851, label: 'O10' },
    { element: 'O', fx: 0.149, fy: 0.5, fz: 0.851, label: 'O11' },
    { element: 'O', fx: 0.851, fy: 0.5, fz: 0.149, label: 'O12' },
    { element: 'O', fx: 0.649, fy: 0.649, fz: 0, label: 'O13' },
    { element: 'O', fx: 0.351, fy: 0.351, fz: 0, label: 'O14' },
    { element: 'O', fx: 0.649, fy: 0.351, fz: 0, label: 'O15' },
    { element: 'O', fx: 0.351, fy: 0.649, fz: 0, label: 'O16' },
    { element: 'O', fx: 0, fy: 0.649, fz: 0.649, label: 'O17' },
    { element: 'O', fx: 0, fy: 0.351, fz: 0.351, label: 'O18' },
    { element: 'O', fx: 0, fy: 0.649, fz: 0.351, label: 'O19' },
    { element: 'O', fx: 0, fy: 0.351, fz: 0.649, label: 'O20' },
    { element: 'O', fx: 0.649, fy: 0, fz: 0.649, label: 'O21' },
    { element: 'O', fx: 0.351, fy: 0, fz: 0.351, label: 'O22' },
    { element: 'O', fx: 0.649, fy: 0, fz: 0.351, label: 'O23' },
    { element: 'O', fx: 0.351, fy: 0, fz: 0.649, label: 'O24' },
  ],
};

// All-silica zeolite A (LTA framework) — the textbook molecular sieve.
// Commercial 3A/4A/5A sieve grades are the K+/Na+/Ca2+ aluminosilicate
// forms; this preset is the all-silica endmember ITQ-29 (Corma et al.
// Nature 2004), which keeps the framework geometry without the cation
// disorder that breaks Pm-3m symmetry in the aluminosilicate.
//
// Geometry: Pm-3m, a = 11.867 Å. T-atoms on the 24k Wyckoff site at
// (0, 0.1842, 0.3724); oxygens split across three sites — O1 at 12h
// (0, 0.2274, 1/2) bridging the 8-ring windows, O2 at 12i (0, 0.2918,
// 0.2918) within the double-4-ring cages, and O3 at 24m (0.1112, 0.1112,
// 0.3447) within the SOD β-cages. Every Si bonds to 1× O1 + 1× O2 +
// 2× O3 at 1.596–1.612 Å, all within ±0.02 Å of the BKS Si-O equilibrium.
//
// Atom count: 24 T + 12 O1 + 12 O2 + 24 O3 = 72 atoms per cell.
// Comfortably under the 200-atom simulation cap at 1×1×1.
export const ZEOLITE_A: CrystalState = {
  name: 'Zeolite A',
  lattice: { a: 11.867, b: 11.867, c: 11.867, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P m -3 m',
  basis: [
    // 24 Si on 24k (0, 0.1842, 0.3724) — full m-3m orbit
    { element: 'Si', fx: 0, fy: 0.1842, fz: 0.3724, label: 'Si1' },
    { element: 'Si', fx: 0, fy: 0.8158, fz: 0.3724, label: 'Si2' },
    { element: 'Si', fx: 0, fy: 0.1842, fz: 0.6276, label: 'Si3' },
    { element: 'Si', fx: 0, fy: 0.8158, fz: 0.6276, label: 'Si4' },
    { element: 'Si', fx: 0, fy: 0.3724, fz: 0.1842, label: 'Si5' },
    { element: 'Si', fx: 0, fy: 0.6276, fz: 0.1842, label: 'Si6' },
    { element: 'Si', fx: 0, fy: 0.3724, fz: 0.8158, label: 'Si7' },
    { element: 'Si', fx: 0, fy: 0.6276, fz: 0.8158, label: 'Si8' },
    { element: 'Si', fx: 0.1842, fy: 0, fz: 0.3724, label: 'Si9' },
    { element: 'Si', fx: 0.8158, fy: 0, fz: 0.3724, label: 'Si10' },
    { element: 'Si', fx: 0.1842, fy: 0, fz: 0.6276, label: 'Si11' },
    { element: 'Si', fx: 0.8158, fy: 0, fz: 0.6276, label: 'Si12' },
    { element: 'Si', fx: 0.3724, fy: 0, fz: 0.1842, label: 'Si13' },
    { element: 'Si', fx: 0.6276, fy: 0, fz: 0.1842, label: 'Si14' },
    { element: 'Si', fx: 0.3724, fy: 0, fz: 0.8158, label: 'Si15' },
    { element: 'Si', fx: 0.6276, fy: 0, fz: 0.8158, label: 'Si16' },
    { element: 'Si', fx: 0.1842, fy: 0.3724, fz: 0, label: 'Si17' },
    { element: 'Si', fx: 0.8158, fy: 0.3724, fz: 0, label: 'Si18' },
    { element: 'Si', fx: 0.1842, fy: 0.6276, fz: 0, label: 'Si19' },
    { element: 'Si', fx: 0.8158, fy: 0.6276, fz: 0, label: 'Si20' },
    { element: 'Si', fx: 0.3724, fy: 0.1842, fz: 0, label: 'Si21' },
    { element: 'Si', fx: 0.6276, fy: 0.1842, fz: 0, label: 'Si22' },
    { element: 'Si', fx: 0.3724, fy: 0.8158, fz: 0, label: 'Si23' },
    { element: 'Si', fx: 0.6276, fy: 0.8158, fz: 0, label: 'Si24' },
    // 12 O1 on 12h (0, 1/2, 0.2274) — bridges 8-ring windows
    { element: 'O', fx: 0, fy: 0.5, fz: 0.2274, label: 'O1' },
    { element: 'O', fx: 0, fy: 0.5, fz: 0.7726, label: 'O2' },
    { element: 'O', fx: 0, fy: 0.2274, fz: 0.5, label: 'O3' },
    { element: 'O', fx: 0, fy: 0.7726, fz: 0.5, label: 'O4' },
    { element: 'O', fx: 0.5, fy: 0, fz: 0.2274, label: 'O5' },
    { element: 'O', fx: 0.5, fy: 0, fz: 0.7726, label: 'O6' },
    { element: 'O', fx: 0.2274, fy: 0.5, fz: 0, label: 'O7' },
    { element: 'O', fx: 0.7726, fy: 0.5, fz: 0, label: 'O8' },
    { element: 'O', fx: 0.2274, fy: 0, fz: 0.5, label: 'O9' },
    { element: 'O', fx: 0.7726, fy: 0, fz: 0.5, label: 'O10' },
    { element: 'O', fx: 0.5, fy: 0.2274, fz: 0, label: 'O11' },
    { element: 'O', fx: 0.5, fy: 0.7726, fz: 0, label: 'O12' },
    // 12 O2 on 12i (0, 0.2918, 0.2918) — within double-4-ring cages
    { element: 'O', fx: 0, fy: 0.2918, fz: 0.2918, label: 'O13' },
    { element: 'O', fx: 0, fy: 0.7082, fz: 0.7082, label: 'O14' },
    { element: 'O', fx: 0, fy: 0.2918, fz: 0.7082, label: 'O15' },
    { element: 'O', fx: 0, fy: 0.7082, fz: 0.2918, label: 'O16' },
    { element: 'O', fx: 0.2918, fy: 0, fz: 0.2918, label: 'O17' },
    { element: 'O', fx: 0.7082, fy: 0, fz: 0.7082, label: 'O18' },
    { element: 'O', fx: 0.2918, fy: 0, fz: 0.7082, label: 'O19' },
    { element: 'O', fx: 0.7082, fy: 0, fz: 0.2918, label: 'O20' },
    { element: 'O', fx: 0.2918, fy: 0.2918, fz: 0, label: 'O21' },
    { element: 'O', fx: 0.7082, fy: 0.7082, fz: 0, label: 'O22' },
    { element: 'O', fx: 0.2918, fy: 0.7082, fz: 0, label: 'O23' },
    { element: 'O', fx: 0.7082, fy: 0.2918, fz: 0, label: 'O24' },
    // 24 O3 on 24m (0.1112, 0.1112, 0.3447) — within SOD β-cages
    { element: 'O', fx: 0.1112, fy: 0.1112, fz: 0.3447, label: 'O25' },
    { element: 'O', fx: 0.8888, fy: 0.1112, fz: 0.3447, label: 'O26' },
    { element: 'O', fx: 0.1112, fy: 0.8888, fz: 0.3447, label: 'O27' },
    { element: 'O', fx: 0.8888, fy: 0.8888, fz: 0.3447, label: 'O28' },
    { element: 'O', fx: 0.1112, fy: 0.1112, fz: 0.6553, label: 'O29' },
    { element: 'O', fx: 0.8888, fy: 0.1112, fz: 0.6553, label: 'O30' },
    { element: 'O', fx: 0.1112, fy: 0.8888, fz: 0.6553, label: 'O31' },
    { element: 'O', fx: 0.8888, fy: 0.8888, fz: 0.6553, label: 'O32' },
    { element: 'O', fx: 0.3447, fy: 0.1112, fz: 0.1112, label: 'O33' },
    { element: 'O', fx: 0.3447, fy: 0.8888, fz: 0.1112, label: 'O34' },
    { element: 'O', fx: 0.3447, fy: 0.1112, fz: 0.8888, label: 'O35' },
    { element: 'O', fx: 0.3447, fy: 0.8888, fz: 0.8888, label: 'O36' },
    { element: 'O', fx: 0.6553, fy: 0.1112, fz: 0.1112, label: 'O37' },
    { element: 'O', fx: 0.6553, fy: 0.8888, fz: 0.1112, label: 'O38' },
    { element: 'O', fx: 0.6553, fy: 0.1112, fz: 0.8888, label: 'O39' },
    { element: 'O', fx: 0.6553, fy: 0.8888, fz: 0.8888, label: 'O40' },
    { element: 'O', fx: 0.1112, fy: 0.3447, fz: 0.1112, label: 'O41' },
    { element: 'O', fx: 0.8888, fy: 0.3447, fz: 0.1112, label: 'O42' },
    { element: 'O', fx: 0.1112, fy: 0.3447, fz: 0.8888, label: 'O43' },
    { element: 'O', fx: 0.8888, fy: 0.3447, fz: 0.8888, label: 'O44' },
    { element: 'O', fx: 0.1112, fy: 0.6553, fz: 0.1112, label: 'O45' },
    { element: 'O', fx: 0.8888, fy: 0.6553, fz: 0.1112, label: 'O46' },
    { element: 'O', fx: 0.1112, fy: 0.6553, fz: 0.8888, label: 'O47' },
    { element: 'O', fx: 0.8888, fy: 0.6553, fz: 0.8888, label: 'O48' },
  ],
};

// Aluminum Antimonide (AlSb) — III-V zincblende, indirect bandgap
// (~1.6 eV). Completes the Al-{P, As, Sb} III-V series. a = 6.1355 Å.
export const ALSB_ZINCBLENDE: CrystalState = {
  name: 'Aluminum Antimonide',
  lattice: { a: 6.1355, b: 6.1355, c: 6.1355, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Al', fx: 0, fy: 0, fz: 0, label: 'Al1' },
    { element: 'Al', fx: 0, fy: 0.5, fz: 0.5, label: 'Al2' },
    { element: 'Al', fx: 0.5, fy: 0, fz: 0.5, label: 'Al3' },
    { element: 'Al', fx: 0.5, fy: 0.5, fz: 0, label: 'Al4' },
    { element: 'Sb', fx: 0.25, fy: 0.25, fz: 0.25, label: 'Sb1' },
    { element: 'Sb', fx: 0.25, fy: 0.75, fz: 0.75, label: 'Sb2' },
    { element: 'Sb', fx: 0.75, fy: 0.25, fz: 0.75, label: 'Sb3' },
    { element: 'Sb', fx: 0.75, fy: 0.75, fz: 0.25, label: 'Sb4' },
  ],
};

// Cinnabar (α-HgS) — the bright-red HgS ore mineral, trigonal P3₁21
// (space group #152), a = 4.149, c = 9.495 Å. Hg-S spiral chains along
// c with a SHORT Hg-S bond ≈ 2.36 Å (each Hg has 2 close S, 4 further),
// distinct from the symmetric tetrahedral coordination of zincblende
// metacinnabar (β-HgS).
//
// CAVEAT: the only HgS force field shipped (Zhou 2013 SW) is fit for
// the CUBIC β-HgS polymorph. Running MD on this trigonal cinnabar
// geometry will distort it — the SW potential's energy minimum is the
// zincblende structure, so cinnabar will gradually relax toward
// metacinnabar during simulation, NOT preserve its trigonal chains.
// For FF-consistent dynamics use the Metacinnabar preset below.
// Cinnabar's proper FF needs an ionic / Hg-S bond model fit to the
// trigonal phase — not currently in the registry.
//
// Hg at Wyckoff 3a (x, 0, 1/3) with x_Hg ≈ 0.720; S at 3b (x, 0, 5/6)
// with x_S ≈ 0.489. Generating positions via the 3a and 3b orbits:
//   3a: (x, 0, 1/3), (0, x, 2/3), (−x, −x, 0)
//   3b: (x, 0, 5/6), (0, x, 1/6), (−x, −x, 1/2)
// (Negative coordinates wrapped into [0, 1) by adding 1.)
export const CINNABAR: CrystalState = {
  name: 'Cinnabar',
  lattice: { a: 4.149, b: 4.149, c: 9.495, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 31 2 1',
  basis: [
    { element: 'Hg', fx: 0.72, fy: 0, fz: 1 / 3, label: 'Hg1' },
    { element: 'Hg', fx: 0, fy: 0.72, fz: 2 / 3, label: 'Hg2' },
    { element: 'Hg', fx: 0.28, fy: 0.28, fz: 0, label: 'Hg3' },
    { element: 'S', fx: 0.489, fy: 0, fz: 5 / 6, label: 'S1' },
    { element: 'S', fx: 0, fy: 0.489, fz: 1 / 6, label: 'S2' },
    { element: 'S', fx: 0.511, fy: 0.511, fz: 0.5, label: 'S3' },
  ],
};

// Metacinnabar (β-HgS) — II-VI zincblende cubic HgS, F-43m, a = 5.851 Å.
// The metastable cubic polymorph of cinnabar. (α-HgS / cinnabar proper
// is trigonal P3₁21, the bright-red ore mineral; metacinnabar is the
// black cubic form. The Zhou 2013 SW pack targets metacinnabar
// specifically — loading the trigonal cinnabar geometry and running MD
// would relax it toward this cubic form.)
export const METACINNABAR: CrystalState = {
  name: 'Metacinnabar',
  lattice: { a: 5.851, b: 5.851, c: 5.851, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Hg', fx: 0, fy: 0, fz: 0, label: 'Hg1' },
    { element: 'Hg', fx: 0, fy: 0.5, fz: 0.5, label: 'Hg2' },
    { element: 'Hg', fx: 0.5, fy: 0, fz: 0.5, label: 'Hg3' },
    { element: 'Hg', fx: 0.5, fy: 0.5, fz: 0, label: 'Hg4' },
    { element: 'S', fx: 0.25, fy: 0.25, fz: 0.25, label: 'S1' },
    { element: 'S', fx: 0.25, fy: 0.75, fz: 0.75, label: 'S2' },
    { element: 'S', fx: 0.75, fy: 0.25, fz: 0.75, label: 'S3' },
    { element: 'S', fx: 0.75, fy: 0.75, fz: 0.25, label: 'S4' },
  ],
};

// Coloradoite (HgTe) — II-VI zincblende F-43m, a = 6.460 Å. Narrow-gap
// semimetal (Eg ≈ 0 eV at room temperature); the end-member of the
// Hg(1-x)Cd(x)Te (MCT) alloy family used for tunable-wavelength infrared
// detectors. Named after the Coloradoite mineral first found in Colorado.
export const COLORADOITE: CrystalState = {
  name: 'Coloradoite',
  lattice: { a: 6.46, b: 6.46, c: 6.46, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Hg', fx: 0, fy: 0, fz: 0, label: 'Hg1' },
    { element: 'Hg', fx: 0, fy: 0.5, fz: 0.5, label: 'Hg2' },
    { element: 'Hg', fx: 0.5, fy: 0, fz: 0.5, label: 'Hg3' },
    { element: 'Hg', fx: 0.5, fy: 0.5, fz: 0, label: 'Hg4' },
    { element: 'Te', fx: 0.25, fy: 0.25, fz: 0.25, label: 'Te1' },
    { element: 'Te', fx: 0.25, fy: 0.75, fz: 0.75, label: 'Te2' },
    { element: 'Te', fx: 0.75, fy: 0.25, fz: 0.75, label: 'Te3' },
    { element: 'Te', fx: 0.75, fy: 0.75, fz: 0.25, label: 'Te4' },
  ],
};

// Zinc Telluride (ZnTe) — II-VI zincblende direct bandgap (~2.26 eV);
// common red-emitting LED host and infrared electro-optic material.
// a = 6.1037 Å.
export const ZNTE_ZINCBLENDE: CrystalState = {
  name: 'Zinc Telluride',
  lattice: { a: 6.1037, b: 6.1037, c: 6.1037, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Zn', fx: 0, fy: 0, fz: 0, label: 'Zn1' },
    { element: 'Zn', fx: 0, fy: 0.5, fz: 0.5, label: 'Zn2' },
    { element: 'Zn', fx: 0.5, fy: 0, fz: 0.5, label: 'Zn3' },
    { element: 'Zn', fx: 0.5, fy: 0.5, fz: 0, label: 'Zn4' },
    { element: 'Te', fx: 0.25, fy: 0.25, fz: 0.25, label: 'Te1' },
    { element: 'Te', fx: 0.25, fy: 0.75, fz: 0.75, label: 'Te2' },
    { element: 'Te', fx: 0.75, fy: 0.25, fz: 0.75, label: 'Te3' },
    { element: 'Te', fx: 0.75, fy: 0.75, fz: 0.25, label: 'Te4' },
  ],
};

// Beryllium Oxide (bromellite, w-BeO) — wurtzite P6_3mc, a = 2.698,
// c = 4.380 Å. Ultra-high thermal conductivity ceramic (~330 W/m·K)
// with a wide bandgap (~10.6 eV). Same wurtzite topology as ZnO / GaN.
export const BEO_WURTZITE: CrystalState = {
  name: 'Beryllium Oxide',
  lattice: { a: 2.698, b: 2.698, c: 4.38, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63 m c',
  basis: [
    { element: 'Be', fx: 1 / 3, fy: 2 / 3, fz: 0, label: 'Be1' },
    { element: 'Be', fx: 2 / 3, fy: 1 / 3, fz: 0.5, label: 'Be2' },
    { element: 'O', fx: 1 / 3, fy: 2 / 3, fz: 0.378, label: 'O1' },
    { element: 'O', fx: 2 / 3, fy: 1 / 3, fz: 0.878, label: 'O2' },
  ],
  elementCharges: { Be: 2, O: -2 },
};

// Aluminum Arsenide (AlAs) — III-V zincblende, common lattice-matched
// partner to GaAs in heterostructures. a = 5.6611 Å.
export const ALAS_ZINCBLENDE: CrystalState = {
  name: 'Aluminum Arsenide',
  lattice: { a: 5.6611, b: 5.6611, c: 5.6611, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Al', fx: 0, fy: 0, fz: 0, label: 'Al1' },
    { element: 'Al', fx: 0, fy: 0.5, fz: 0.5, label: 'Al2' },
    { element: 'Al', fx: 0.5, fy: 0, fz: 0.5, label: 'Al3' },
    { element: 'Al', fx: 0.5, fy: 0.5, fz: 0, label: 'Al4' },
    { element: 'As', fx: 0.25, fy: 0.25, fz: 0.25, label: 'As1' },
    { element: 'As', fx: 0.25, fy: 0.75, fz: 0.75, label: 'As2' },
    { element: 'As', fx: 0.75, fy: 0.25, fz: 0.75, label: 'As3' },
    { element: 'As', fx: 0.75, fy: 0.75, fz: 0.25, label: 'As4' },
  ],
};

// Gallium Antimonide (GaSb) — III-V zincblende, narrow direct bandgap
// (~0.73 eV); IR optoelectronics. a = 6.0959 Å.
export const GASB_ZINCBLENDE: CrystalState = {
  name: 'Gallium Antimonide',
  lattice: { a: 6.0959, b: 6.0959, c: 6.0959, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Ga', fx: 0, fy: 0, fz: 0, label: 'Ga1' },
    { element: 'Ga', fx: 0, fy: 0.5, fz: 0.5, label: 'Ga2' },
    { element: 'Ga', fx: 0.5, fy: 0, fz: 0.5, label: 'Ga3' },
    { element: 'Ga', fx: 0.5, fy: 0.5, fz: 0, label: 'Ga4' },
    { element: 'Sb', fx: 0.25, fy: 0.25, fz: 0.25, label: 'Sb1' },
    { element: 'Sb', fx: 0.25, fy: 0.75, fz: 0.75, label: 'Sb2' },
    { element: 'Sb', fx: 0.75, fy: 0.25, fz: 0.75, label: 'Sb3' },
    { element: 'Sb', fx: 0.75, fy: 0.75, fz: 0.25, label: 'Sb4' },
  ],
};

// Boron Phosphide (BP) — III-V zincblende, indirect bandgap. Lightest
// of the B-containing III-Vs; high hardness and thermal conductivity.
// a = 4.5383 Å, B-P ≈ 1.97 Å.
export const BP_ZINCBLENDE: CrystalState = {
  name: 'Boron Phosphide',
  lattice: { a: 4.5383, b: 4.5383, c: 4.5383, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'B', fx: 0, fy: 0, fz: 0, label: 'B1' },
    { element: 'B', fx: 0, fy: 0.5, fz: 0.5, label: 'B2' },
    { element: 'B', fx: 0.5, fy: 0, fz: 0.5, label: 'B3' },
    { element: 'B', fx: 0.5, fy: 0.5, fz: 0, label: 'B4' },
    { element: 'P', fx: 0.25, fy: 0.25, fz: 0.25, label: 'P1' },
    { element: 'P', fx: 0.25, fy: 0.75, fz: 0.75, label: 'P2' },
    { element: 'P', fx: 0.75, fy: 0.25, fz: 0.75, label: 'P3' },
    { element: 'P', fx: 0.75, fy: 0.75, fz: 0.25, label: 'P4' },
  ],
};

// Boron Arsenide (BAs) — III-V zincblende notable for an unusually high
// thermal conductivity (~1300 W/m·K, comparable to diamond). a = 4.777 Å.
export const BAS_ZINCBLENDE: CrystalState = {
  name: 'Boron Arsenide',
  lattice: { a: 4.777, b: 4.777, c: 4.777, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'B', fx: 0, fy: 0, fz: 0, label: 'B1' },
    { element: 'B', fx: 0, fy: 0.5, fz: 0.5, label: 'B2' },
    { element: 'B', fx: 0.5, fy: 0, fz: 0.5, label: 'B3' },
    { element: 'B', fx: 0.5, fy: 0.5, fz: 0, label: 'B4' },
    { element: 'As', fx: 0.25, fy: 0.25, fz: 0.25, label: 'As1' },
    { element: 'As', fx: 0.25, fy: 0.75, fz: 0.75, label: 'As2' },
    { element: 'As', fx: 0.75, fy: 0.25, fz: 0.75, label: 'As3' },
    { element: 'As', fx: 0.75, fy: 0.75, fz: 0.25, label: 'As4' },
  ],
};

// Wurtzite ZnS (β-Zinc Sulfide, P6_3mc) — hexagonal polymorph of cubic
// sphalerite ZnS; same local tetrahedral coordination, different long-
// range stacking. a = 3.82, c = 6.26 Å.
export const ZNS_WURTZITE: CrystalState = {
  name: 'Wurtzite Zinc Sulfide',
  lattice: { a: 3.82, b: 3.82, c: 6.26, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63 m c',
  basis: [
    { element: 'Zn', fx: 1 / 3, fy: 2 / 3, fz: 0, label: 'Zn1' },
    { element: 'Zn', fx: 2 / 3, fy: 1 / 3, fz: 0.5, label: 'Zn2' },
    { element: 'S', fx: 1 / 3, fy: 2 / 3, fz: 0.375, label: 'S1' },
    { element: 'S', fx: 2 / 3, fy: 1 / 3, fz: 0.875, label: 'S2' },
  ],
};

// Pyrite FeS₂ — cubic Pa-3 with Fe at FCC sites and S₂²⁻ disulfide
// dimers along body diagonals (u parameter ≈ 0.385). Notable for the
// covalent S-S bonds inside an otherwise-ionic framework. Left without
// elementCharges so the bond-detection rule treats it as covalent —
// otherwise the same-sign filter for ionic mode would suppress the S-S
// disulfide bond, which is exactly the structural signature we want to
// show. ESP coloring is the trade-off.
export const PYRITE_FES2: CrystalState = {
  name: 'Pyrite',
  lattice: { a: 5.418, b: 5.418, c: 5.418, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P a -3',
  basis: [
    { element: 'Fe', fx: 0, fy: 0, fz: 0, label: 'Fe1' },
    { element: 'Fe', fx: 0.5, fy: 0.5, fz: 0, label: 'Fe2' },
    { element: 'Fe', fx: 0.5, fy: 0, fz: 0.5, label: 'Fe3' },
    { element: 'Fe', fx: 0, fy: 0.5, fz: 0.5, label: 'Fe4' },
    // S 8c at (u, u, u) with u = 0.385; symmetry expansion of Pa-3.
    { element: 'S', fx: 0.385, fy: 0.385, fz: 0.385, label: 'S1' },
    { element: 'S', fx: 0.615, fy: 0.615, fz: 0.615, label: 'S2' },
    { element: 'S', fx: 0.115, fy: 0.615, fz: 0.885, label: 'S3' },
    { element: 'S', fx: 0.885, fy: 0.385, fz: 0.115, label: 'S4' },
    { element: 'S', fx: 0.615, fy: 0.885, fz: 0.115, label: 'S5' },
    { element: 'S', fx: 0.385, fy: 0.115, fz: 0.885, label: 'S6' },
    { element: 'S', fx: 0.885, fy: 0.115, fz: 0.615, label: 'S7' },
    { element: 'S', fx: 0.115, fy: 0.885, fz: 0.385, label: 'S8' },
  ],
};

// Anatase TiO₂ — tetragonal polymorph of rutile (I4_1/amd). Same chemistry
// as rutile, different structure: each Ti is still octahedrally coordinated
// by 6 O, but the edge-sharing pattern differs. Ti at 4a, O at 8e with
// z ≈ 0.208. Useful for showing crystal polymorphism side by side with
// rutile.
export const ANATASE_TIO2: CrystalState = {
  name: 'Anatase',
  lattice: { a: 3.7845, b: 3.7845, c: 9.5143, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'I 41/a m d',
  basis: [
    { element: 'Ti', fx: 0, fy: 0, fz: 0, label: 'Ti1' },
    { element: 'Ti', fx: 0, fy: 0.5, fz: 0.25, label: 'Ti2' },
    { element: 'Ti', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Ti3' },
    { element: 'Ti', fx: 0.5, fy: 0, fz: 0.75, label: 'Ti4' },
    { element: 'O', fx: 0, fy: 0, fz: 0.208, label: 'O1' },
    { element: 'O', fx: 0, fy: 0, fz: 0.792, label: 'O2' },
    { element: 'O', fx: 0, fy: 0.5, fz: 0.458, label: 'O3' },
    { element: 'O', fx: 0, fy: 0.5, fz: 0.042, label: 'O4' },
    { element: 'O', fx: 0.5, fy: 0.5, fz: 0.708, label: 'O5' },
    { element: 'O', fx: 0.5, fy: 0.5, fz: 0.292, label: 'O6' },
    { element: 'O', fx: 0.5, fy: 0, fz: 0.958, label: 'O7' },
    { element: 'O', fx: 0.5, fy: 0, fz: 0.542, label: 'O8' },
  ],
  elementCharges: { Ti: 4, O: -2 },
};

// Aluminum FCC — the third canonical close-packed metal alongside Cu (FCC)
// and Fe (BCC). a = 4.0495 Å at RT.
export const ALUMINUM_FCC: CrystalState = {
  name: 'Aluminum',
  lattice: { a: 4.0495, b: 4.0495, c: 4.0495, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Al', fx: 0, fy: 0, fz: 0, label: 'Al1' },
    { element: 'Al', fx: 0, fy: 0.5, fz: 0.5, label: 'Al2' },
    { element: 'Al', fx: 0.5, fy: 0, fz: 0.5, label: 'Al3' },
    { element: 'Al', fx: 0.5, fy: 0.5, fz: 0, label: 'Al4' },
  ],
  metallic: true,
};

// Gold FCC — noble-metal reference; same Fm-3m as Cu and Al but with a
// larger lattice constant. a = 4.0782 Å.
export const GOLD_FCC: CrystalState = {
  name: 'Gold',
  lattice: { a: 4.0782, b: 4.0782, c: 4.0782, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Au', fx: 0, fy: 0, fz: 0, label: 'Au1' },
    { element: 'Au', fx: 0, fy: 0.5, fz: 0.5, label: 'Au2' },
    { element: 'Au', fx: 0.5, fy: 0, fz: 0.5, label: 'Au3' },
    { element: 'Au', fx: 0.5, fy: 0.5, fz: 0, label: 'Au4' },
  ],
  metallic: true,
};

// Tungsten BCC — refractory transition metal. Same Im-3m structure as
// α-iron at a smaller lattice constant (a = 3.1652 Å). Highest melting
// point of all metals (~3422 °C).
export const TUNGSTEN_BCC: CrystalState = {
  name: 'Tungsten',
  lattice: { a: 3.1652, b: 3.1652, c: 3.1652, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'I m -3 m',
  basis: [
    { element: 'W', fx: 0, fy: 0, fz: 0, label: 'W1' },
    { element: 'W', fx: 0.5, fy: 0.5, fz: 0.5, label: 'W2' },
  ],
  metallic: true,
};

// Lithium BCC — lightest alkali metal. Im-3m at RT, a = 3.491 Å. Pairs
// with Cs/Rb/Na in the alkali-metal series and counterpoints W/Fe at the
// other end of the BCC stiffness/density range.
export const LITHIUM_BCC: CrystalState = {
  name: 'Lithium',
  lattice: { a: 3.491, b: 3.491, c: 3.491, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'I m -3 m',
  basis: [
    { element: 'Li', fx: 0, fy: 0, fz: 0, label: 'Li1' },
    { element: 'Li', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Li2' },
  ],
  metallic: true,
};

// Zinc HCP — a = 2.665, c = 4.947 Å. c/a ≈ 1.856 is well above the ideal
// 1.633, so visually the cell is noticeably "tall" compared to Mg HCP
// (1.624). Good textbook example of a non-ideal c/a HCP.
export const ZINC_HCP: CrystalState = {
  name: 'Zinc',
  lattice: { a: 2.665, b: 2.665, c: 4.947, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63/m m c',
  basis: [
    { element: 'Zn', fx: 1 / 3, fy: 2 / 3, fz: 0.25, label: 'Zn1' },
    { element: 'Zn', fx: 2 / 3, fy: 1 / 3, fz: 0.75, label: 'Zn2' },
  ],
  metallic: true,
  conventionalCell: 'hexagonal-prism',
};

// Germanium — same diamond topology as Si and C, with a = 5.658 Å. The
// third group-14 covalent benchmark. Indirect-gap semiconductor; useful
// for showing that the structure is identical to silicon despite the
// different optoelectronics.
export const GERMANIUM: CrystalState = {
  name: 'Germanium',
  lattice: { a: 5.658, b: 5.658, c: 5.658, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F d -3 m',
  basis: [
    { element: 'Ge', fx: 0, fy: 0, fz: 0, label: 'Ge1' },
    { element: 'Ge', fx: 0, fy: 0.5, fz: 0.5, label: 'Ge2' },
    { element: 'Ge', fx: 0.5, fy: 0, fz: 0.5, label: 'Ge3' },
    { element: 'Ge', fx: 0.5, fy: 0.5, fz: 0, label: 'Ge4' },
    { element: 'Ge', fx: 0.25, fy: 0.25, fz: 0.25, label: 'Ge5' },
    { element: 'Ge', fx: 0.25, fy: 0.75, fz: 0.75, label: 'Ge6' },
    { element: 'Ge', fx: 0.75, fy: 0.25, fz: 0.75, label: 'Ge7' },
    { element: 'Ge', fx: 0.75, fy: 0.75, fz: 0.25, label: 'Ge8' },
  ],
};

// Lead Sulfide (galena) — heavy-atom rocksalt structure, a = 5.9362 Å.
// Same Fm-3m topology as NaCl / MgO; useful for showing that the
// rock-salt arrangement scales from light alkali halides up to
// semiconductor heavy metals (PbTe, PbSe also share this lattice type).
export const LEAD_SULFIDE: CrystalState = {
  name: 'Lead Sulfide',
  lattice: { a: 5.9362, b: 5.9362, c: 5.9362, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Pb', fx: 0, fy: 0, fz: 0, label: 'Pb1' },
    { element: 'Pb', fx: 0, fy: 0.5, fz: 0.5, label: 'Pb2' },
    { element: 'Pb', fx: 0.5, fy: 0, fz: 0.5, label: 'Pb3' },
    { element: 'Pb', fx: 0.5, fy: 0.5, fz: 0, label: 'Pb4' },
    { element: 'S', fx: 0.5, fy: 0.5, fz: 0.5, label: 'S1' },
    { element: 'S', fx: 0.5, fy: 0, fz: 0, label: 'S2' },
    { element: 'S', fx: 0, fy: 0.5, fz: 0, label: 'S3' },
    { element: 'S', fx: 0, fy: 0, fz: 0.5, label: 'S4' },
  ],
  elementCharges: { Pb: 2, S: -2 },
};

// Zinc Sulfide (sphalerite, cubic ZnS) — the prototype zincblende
// structure that GaAs / c-BN are named after. a = 5.4109 Å.
export const ZINC_SULFIDE: CrystalState = {
  name: 'Zinc Sulfide',
  lattice: { a: 5.4109, b: 5.4109, c: 5.4109, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Zn', fx: 0, fy: 0, fz: 0, label: 'Zn1' },
    { element: 'Zn', fx: 0, fy: 0.5, fz: 0.5, label: 'Zn2' },
    { element: 'Zn', fx: 0.5, fy: 0, fz: 0.5, label: 'Zn3' },
    { element: 'Zn', fx: 0.5, fy: 0.5, fz: 0, label: 'Zn4' },
    { element: 'S', fx: 0.25, fy: 0.25, fz: 0.25, label: 'S1' },
    { element: 'S', fx: 0.25, fy: 0.75, fz: 0.75, label: 'S2' },
    { element: 'S', fx: 0.75, fy: 0.25, fz: 0.75, label: 'S3' },
    { element: 'S', fx: 0.75, fy: 0.75, fz: 0.25, label: 'S4' },
  ],
};

// Gallium Nitride (wurtzite GaN) — III-V wide-bandgap semiconductor at
// the heart of blue/UV LEDs and high-power electronics. Wurtzite cell
// with a = 3.189, c = 5.185 Å, u(N) ≈ 0.377.
export const GALLIUM_NITRIDE: CrystalState = {
  name: 'Gallium Nitride',
  lattice: { a: 3.189, b: 3.189, c: 5.185, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63 m c',
  basis: [
    { element: 'Ga', fx: 1 / 3, fy: 2 / 3, fz: 0, label: 'Ga1' },
    { element: 'Ga', fx: 2 / 3, fy: 1 / 3, fz: 0.5, label: 'Ga2' },
    { element: 'N', fx: 1 / 3, fy: 2 / 3, fz: 0.377, label: 'N1' },
    { element: 'N', fx: 2 / 3, fy: 1 / 3, fz: 0.877, label: 'N2' },
  ],
};

// Lithium Fluoride — small-ion rocksalt, a = 4.0270 Å. Strong ionic
// character; pairs nicely with NaCl/MgO at the low-Z end of the alkali-
// halide / alkaline-earth-chalcogenide series.
export const LITHIUM_FLUORIDE: CrystalState = {
  name: 'Lithium Fluoride',
  lattice: { a: 4.027, b: 4.027, c: 4.027, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Li', fx: 0, fy: 0, fz: 0, label: 'Li1' },
    { element: 'Li', fx: 0, fy: 0.5, fz: 0.5, label: 'Li2' },
    { element: 'Li', fx: 0.5, fy: 0, fz: 0.5, label: 'Li3' },
    { element: 'Li', fx: 0.5, fy: 0.5, fz: 0, label: 'Li4' },
    { element: 'F', fx: 0.5, fy: 0.5, fz: 0.5, label: 'F1' },
    { element: 'F', fx: 0.5, fy: 0, fz: 0, label: 'F2' },
    { element: 'F', fx: 0, fy: 0.5, fz: 0, label: 'F3' },
    { element: 'F', fx: 0, fy: 0, fz: 0.5, label: 'F4' },
  ],
  elementCharges: { Li: 1, F: -1 },
};

// Cesium Iodide — heavy CsCl-type, a = 4.567 Å. Larger ions than CsCl
// itself, used in X-ray / γ-ray scintillators. Same simple-cubic
// arrangement, just bigger.
export const CESIUM_IODIDE: CrystalState = {
  name: 'Cesium Iodide',
  lattice: { a: 4.567, b: 4.567, c: 4.567, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P m -3 m',
  basis: [
    { element: 'Cs', fx: 0, fy: 0, fz: 0, label: 'Cs1' },
    { element: 'I', fx: 0.5, fy: 0.5, fz: 0.5, label: 'I1' },
  ],
  elementCharges: { Cs: 1, I: -1 },
};

// Cu₃Au (L1₂ ordered FCC intermetallic) — Pm-3m, a = 3.7491 Å. Classic
// order/disorder example: above 390 °C the alloy is disordered FCC; below,
// Au atoms occupy the cube corners and Cu atoms the face centers, giving
// a 1:3 Au:Cu ordered superlattice.
export const CU3AU_L12: CrystalState = {
  name: 'Copper-Gold (Cu3Au)',
  lattice: { a: 3.7491, b: 3.7491, c: 3.7491, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P m -3 m',
  basis: [
    { element: 'Au', fx: 0, fy: 0, fz: 0, label: 'Au1' },
    { element: 'Cu', fx: 0.5, fy: 0.5, fz: 0, label: 'Cu1' },
    { element: 'Cu', fx: 0.5, fy: 0, fz: 0.5, label: 'Cu2' },
    { element: 'Cu', fx: 0, fy: 0.5, fz: 0.5, label: 'Cu3' },
  ],
  metallic: true,
};

// Au₃Cu (L1₂ ordered FCC intermetallic) — Pm-3m, a ≈ 3.96 Å. Inverse
// stoichiometry of Cu₃Au with Cu at the corners and Au at the face
// centers. Less common than Cu₃Au but completes the symmetric pair.
export const AU3CU_L12: CrystalState = {
  name: 'Gold-Copper (Au3Cu)',
  lattice: { a: 3.96, b: 3.96, c: 3.96, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P m -3 m',
  basis: [
    { element: 'Cu', fx: 0, fy: 0, fz: 0, label: 'Cu1' },
    { element: 'Au', fx: 0.5, fy: 0.5, fz: 0, label: 'Au1' },
    { element: 'Au', fx: 0.5, fy: 0, fz: 0.5, label: 'Au2' },
    { element: 'Au', fx: 0, fy: 0.5, fz: 0.5, label: 'Au3' },
  ],
  metallic: true,
};

// CuAu (γ'-CuAu, L1₀ ordered tetragonal) — P4/mmm, a = b = 3.97, c = 3.68 Å
// (c/a ≈ 0.93). 1:1 stoichiometry with alternating (001) planes of Cu and
// Au. The slight tetragonal distortion is the order parameter — above
// ~410 °C the alloy is cubic disordered FCC.
export const CUAU_L10: CrystalState = {
  name: 'Copper-Gold (CuAu)',
  lattice: { a: 3.97, b: 3.97, c: 3.68, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P 4/m m m',
  basis: [
    { element: 'Cu', fx: 0, fy: 0, fz: 0, label: 'Cu1' },
    { element: 'Cu', fx: 0.5, fy: 0.5, fz: 0, label: 'Cu2' },
    { element: 'Au', fx: 0.5, fy: 0, fz: 0.5, label: 'Au1' },
    { element: 'Au', fx: 0, fy: 0.5, fz: 0.5, label: 'Au2' },
  ],
  metallic: true,
};

// β-Brass (β-CuZn, B2 ordered) — Pm-3m, a = 2.948 Å. Below ~470 °C the
// 1:1 Cu-Zn alloy orders into the CsCl-type structure with Cu at the
// corners and Zn at the body center; above, it disorders into BCC.
// One of the textbook examples of an order/disorder transition.
export const BETA_BRASS: CrystalState = {
  name: 'Beta-Brass',
  lattice: { a: 2.948, b: 2.948, c: 2.948, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P m -3 m',
  basis: [
    { element: 'Cu', fx: 0, fy: 0, fz: 0, label: 'Cu1' },
    { element: 'Zn', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Zn1' },
  ],
  metallic: true,
};

// NiAl (B2 ordered intermetallic) — Pm-3m, a = 2.887 Å. CsCl-type
// structure with Ni at corners, Al at body center. High-temperature
// structural alloy and a model β-phase of the Ni-Al system.
export const NIAL_B2: CrystalState = {
  name: 'Nickel Aluminide',
  lattice: { a: 2.887, b: 2.887, c: 2.887, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P m -3 m',
  basis: [
    { element: 'Ni', fx: 0, fy: 0, fz: 0, label: 'Ni1' },
    { element: 'Al', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Al1' },
  ],
  metallic: true,
};

// Ni₃Al (γ' phase, L1₂ ordered FCC) — Pm-3m, a = 3.572 Å. The strengthening
// precipitate in nickel-based superalloys (jet-engine turbine blades).
// Al at the cube corners, Ni at the face centers — same topology as
// Cu₃Au.
export const NI3AL_L12: CrystalState = {
  name: 'Nickel Aluminide (Ni3Al)',
  lattice: { a: 3.572, b: 3.572, c: 3.572, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P m -3 m',
  basis: [
    { element: 'Al', fx: 0, fy: 0, fz: 0, label: 'Al1' },
    { element: 'Ni', fx: 0.5, fy: 0.5, fz: 0, label: 'Ni1' },
    { element: 'Ni', fx: 0.5, fy: 0, fz: 0.5, label: 'Ni2' },
    { element: 'Ni', fx: 0, fy: 0.5, fz: 0.5, label: 'Ni3' },
  ],
  metallic: true,
};

// NiAl₃ (ε-Al₃Ni, D0₂₀ cementite-type) — Pnma (No. 62), orthorhombic, 16
// atoms/cell (12 Al + 4 Ni). Unlike its simple-cubic Ni-Al cousins (NiAl is a
// 2-atom B2, Ni₃Al a 4-atom L1₂), this Al-rich line compound is a complex
// 16-atom orthorhombic structure — exactly the kind an LLM reconstructs
// incorrectly, so it is curated here rather than left to generation. The basis
// is the Materials Project DFT-relaxed cell (mp-622209), listed explicitly in
// P1 form: the basis IS the complete conventional cell (the builder does not
// apply space-group operators). Axis order follows that cell (a < b < c, the
// mirror plane ⊥ c, so the special sites carry fz = ¼/¾), a valid Pnma setting.
// FF: the {Al,Ni} Sutton-Chen pack (eamParameters.ts) covers any Ni-Al
// composition, so MD works without a new potential.
export const NIAL3_D020: CrystalState = {
  name: 'Nickel Aluminide (NiAl3)',
  lattice: { a: 4.80689, b: 6.55682, c: 7.29431, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P n m a',
  basis: [
    { element: 'Al', fx: 0.91517, fy: 0.00857, fz: 0.25, label: 'Al1' },
    { element: 'Al', fx: 0.36106, fy: 0.17143, fz: 0.44542, label: 'Al2' },
    { element: 'Al', fx: 0.36106, fy: 0.17143, fz: 0.05458, label: 'Al3' },
    { element: 'Al', fx: 0.86106, fy: 0.32857, fz: 0.55458, label: 'Al4' },
    { element: 'Al', fx: 0.13894, fy: 0.67143, fz: 0.44542, label: 'Al5' },
    { element: 'Al', fx: 0.63894, fy: 0.82857, fz: 0.94542, label: 'Al6' },
    { element: 'Al', fx: 0.13894, fy: 0.67143, fz: 0.05458, label: 'Al7' },
    { element: 'Al', fx: 0.63894, fy: 0.82857, fz: 0.55458, label: 'Al8' },
    { element: 'Al', fx: 0.86106, fy: 0.32857, fz: 0.94542, label: 'Al9' },
    { element: 'Al', fx: 0.58483, fy: 0.50857, fz: 0.25, label: 'Al10' },
    { element: 'Al', fx: 0.08483, fy: 0.99143, fz: 0.75, label: 'Al11' },
    { element: 'Al', fx: 0.41517, fy: 0.49143, fz: 0.75, label: 'Al12' },
    { element: 'Ni', fx: 0.44384, fy: 0.87126, fz: 0.25, label: 'Ni1' },
    { element: 'Ni', fx: 0.05616, fy: 0.37126, fz: 0.25, label: 'Ni2' },
    { element: 'Ni', fx: 0.55616, fy: 0.12874, fz: 0.75, label: 'Ni3' },
    { element: 'Ni', fx: 0.94384, fy: 0.62874, fz: 0.75, label: 'Ni4' },
  ],
  metallic: true,
};

// Chromium BCC — Im-3m, a = 2.91 Å. Antiferromagnetic at room temperature
// (Néel ≈ 311 K). Hard, corrosion-resistant — chrome-plating and stainless
// steel parent metal.
export const CHROMIUM_BCC: CrystalState = {
  name: 'Chromium',
  lattice: { a: 2.91, b: 2.91, c: 2.91, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'I m -3 m',
  basis: [
    { element: 'Cr', fx: 0, fy: 0, fz: 0, label: 'Cr1' },
    { element: 'Cr', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Cr2' },
  ],
  metallic: true,
};

// Tantalum BCC — Im-3m, a = 3.3013 Å. High-density refractory metal
// (m.p. 3017 °C); used in electrolytic capacitors for its stable native
// Ta₂O₅ dielectric layer.
export const TANTALUM_BCC: CrystalState = {
  name: 'Tantalum',
  lattice: { a: 3.3013, b: 3.3013, c: 3.3013, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'I m -3 m',
  basis: [
    { element: 'Ta', fx: 0, fy: 0, fz: 0, label: 'Ta1' },
    { element: 'Ta', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Ta2' },
  ],
  metallic: true,
};

// Molybdenum BCC — Im-3m, a = 3.1472 Å. Refractory transition metal;
// strengthening alloying element in high-strength steels and superalloys.
export const MOLYBDENUM_BCC: CrystalState = {
  name: 'Molybdenum',
  lattice: { a: 3.1472, b: 3.1472, c: 3.1472, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'I m -3 m',
  basis: [
    { element: 'Mo', fx: 0, fy: 0, fz: 0, label: 'Mo1' },
    { element: 'Mo', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Mo2' },
  ],
  metallic: true,
};

// Palladium FCC — Fm-3m, a = 3.8907 Å. Catalytic noble metal; absorbs up
// to 900× its volume in hydrogen. Pairs with Pt / Ni in the FCC
// platinum-group metal series.
export const PALLADIUM_FCC: CrystalState = {
  name: 'Palladium',
  lattice: { a: 3.8907, b: 3.8907, c: 3.8907, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Pd', fx: 0, fy: 0, fz: 0, label: 'Pd1' },
    { element: 'Pd', fx: 0, fy: 0.5, fz: 0.5, label: 'Pd2' },
    { element: 'Pd', fx: 0.5, fy: 0, fz: 0.5, label: 'Pd3' },
    { element: 'Pd', fx: 0.5, fy: 0.5, fz: 0, label: 'Pd4' },
  ],
  metallic: true,
};

// Zirconium HCP (α-Zr) — P6_3/mmc, a = 3.2316, c = 5.1475 Å, c/a ≈ 1.593
// (below ideal 1.633). Low neutron-capture cross-section makes its
// alloys (Zircaloy) the standard cladding for nuclear fuel rods.
export const ZIRCONIUM_HCP: CrystalState = {
  name: 'Zirconium',
  lattice: { a: 3.2316, b: 3.2316, c: 5.1475, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63/m m c',
  basis: [
    { element: 'Zr', fx: 1 / 3, fy: 2 / 3, fz: 0.25, label: 'Zr1' },
    { element: 'Zr', fx: 2 / 3, fy: 1 / 3, fz: 0.75, label: 'Zr2' },
  ],
  metallic: true,
  conventionalCell: 'hexagonal-prism',
};

// Cadmium Sulfide (greenockite, wurtzite β-CdS) — P6_3mc, a = 4.136,
// c = 6.713 Å. II-VI direct-gap semiconductor (~2.42 eV); historical
// photoconductive material in early light meters and CdS-CdTe solar
// cells. Same wurtzite topology as ZnO / GaN / AlN with Cd²⁺ / S²⁻.
export const CDS_WURTZITE: CrystalState = {
  name: 'Cadmium Sulfide',
  lattice: { a: 4.136, b: 4.136, c: 6.713, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63 m c',
  basis: [
    { element: 'Cd', fx: 1 / 3, fy: 2 / 3, fz: 0, label: 'Cd1' },
    { element: 'Cd', fx: 2 / 3, fy: 1 / 3, fz: 0.5, label: 'Cd2' },
    { element: 'S', fx: 1 / 3, fy: 2 / 3, fz: 0.377, label: 'S1' },
    { element: 'S', fx: 2 / 3, fy: 1 / 3, fz: 0.877, label: 'S2' },
  ],
  elementCharges: { Cd: 2, S: -2 },
};

// Manganese Oxide (manganosite, MnO) — rocksalt, a = 4.445 Å. Type-II
// antiferromagnet (Néel ≈ 116 K), the first material in which neutron
// diffraction (Shull 1949) directly imaged a magnetic structure.
export const MNO_ROCKSALT: CrystalState = {
  name: 'Manganese Oxide',
  lattice: { a: 4.445, b: 4.445, c: 4.445, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Mn', fx: 0, fy: 0, fz: 0, label: 'Mn1' },
    { element: 'Mn', fx: 0, fy: 0.5, fz: 0.5, label: 'Mn2' },
    { element: 'Mn', fx: 0.5, fy: 0, fz: 0.5, label: 'Mn3' },
    { element: 'Mn', fx: 0.5, fy: 0.5, fz: 0, label: 'Mn4' },
    { element: 'O', fx: 0.5, fy: 0.5, fz: 0.5, label: 'O1' },
    { element: 'O', fx: 0.5, fy: 0, fz: 0, label: 'O2' },
    { element: 'O', fx: 0, fy: 0.5, fz: 0, label: 'O3' },
    { element: 'O', fx: 0, fy: 0, fz: 0.5, label: 'O4' },
  ],
  elementCharges: { Mn: 2, O: -2 },
};

// Nickel Oxide (bunsenite, NiO) — rocksalt, a = 4.177 Å. Mott-Hubbard
// antiferromagnetic insulator (Néel temperature ~525 K) — classic test
// case for strongly-correlated electron physics. Same Fm-3m topology as
// MgO / CaO / FeO / MnO.
export const NIO_ROCKSALT: CrystalState = {
  name: 'Nickel Oxide',
  lattice: { a: 4.177, b: 4.177, c: 4.177, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Ni', fx: 0, fy: 0, fz: 0, label: 'Ni1' },
    { element: 'Ni', fx: 0, fy: 0.5, fz: 0.5, label: 'Ni2' },
    { element: 'Ni', fx: 0.5, fy: 0, fz: 0.5, label: 'Ni3' },
    { element: 'Ni', fx: 0.5, fy: 0.5, fz: 0, label: 'Ni4' },
    { element: 'O', fx: 0.5, fy: 0.5, fz: 0.5, label: 'O1' },
    { element: 'O', fx: 0.5, fy: 0, fz: 0, label: 'O2' },
    { element: 'O', fx: 0, fy: 0.5, fz: 0, label: 'O3' },
    { element: 'O', fx: 0, fy: 0, fz: 0.5, label: 'O4' },
  ],
  elementCharges: { Ni: 2, O: -2 },
};

// Lead Titanate (PbTiO₃) — cubic perovskite, Pm-3m, a = 3.97 Å above the
// ~490 °C ferroelectric Curie point; below it distorts to tetragonal
// P4mm with a spontaneous polarization. Important end-member of the
// PZT (Pb(Zr,Ti)O₃) piezoelectric family.
export const PBTIO3_PEROVSKITE: CrystalState = {
  name: 'Lead Titanate',
  lattice: { a: 3.97, b: 3.97, c: 3.97, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P m -3 m',
  basis: [
    { element: 'Pb', fx: 0, fy: 0, fz: 0, label: 'Pb1' },
    { element: 'Ti', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Ti1' },
    { element: 'O', fx: 0.5, fy: 0.5, fz: 0, label: 'O1' },
    { element: 'O', fx: 0.5, fy: 0, fz: 0.5, label: 'O2' },
    { element: 'O', fx: 0, fy: 0.5, fz: 0.5, label: 'O3' },
  ],
  elementCharges: { Pb: 2, Ti: 4, O: -2 },
};

// Nickel FCC — Fm-3m, a = 3.524 Å. Common transition metal; pairs with
// Cu/Al/Au at the FCC end of the metal series.
export const NICKEL_FCC: CrystalState = {
  name: 'Nickel',
  lattice: { a: 3.524, b: 3.524, c: 3.524, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Ni', fx: 0, fy: 0, fz: 0, label: 'Ni1' },
    { element: 'Ni', fx: 0, fy: 0.5, fz: 0.5, label: 'Ni2' },
    { element: 'Ni', fx: 0.5, fy: 0, fz: 0.5, label: 'Ni3' },
    { element: 'Ni', fx: 0.5, fy: 0.5, fz: 0, label: 'Ni4' },
  ],
  metallic: true,
};

// Silver FCC — Fm-3m, a = 4.0853 Å. Noble metal; counterpoint to Au.
export const SILVER_FCC: CrystalState = {
  name: 'Silver',
  lattice: { a: 4.0853, b: 4.0853, c: 4.0853, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Ag', fx: 0, fy: 0, fz: 0, label: 'Ag1' },
    { element: 'Ag', fx: 0, fy: 0.5, fz: 0.5, label: 'Ag2' },
    { element: 'Ag', fx: 0.5, fy: 0, fz: 0.5, label: 'Ag3' },
    { element: 'Ag', fx: 0.5, fy: 0.5, fz: 0, label: 'Ag4' },
  ],
  metallic: true,
};

// Platinum FCC — Fm-3m, a = 3.9239 Å. Catalytic noble metal.
export const PLATINUM_FCC: CrystalState = {
  name: 'Platinum',
  lattice: { a: 3.9239, b: 3.9239, c: 3.9239, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Pt', fx: 0, fy: 0, fz: 0, label: 'Pt1' },
    { element: 'Pt', fx: 0, fy: 0.5, fz: 0.5, label: 'Pt2' },
    { element: 'Pt', fx: 0.5, fy: 0, fz: 0.5, label: 'Pt3' },
    { element: 'Pt', fx: 0.5, fy: 0.5, fz: 0, label: 'Pt4' },
  ],
  metallic: true,
};

// Sodium BCC — Im-3m, a = 4.291 Å. Light alkali metal; counterpoint to
// Li (also BCC) and Cs at the other end of the alkali series.
export const SODIUM_BCC: CrystalState = {
  name: 'Sodium',
  lattice: { a: 4.291, b: 4.291, c: 4.291, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'I m -3 m',
  basis: [
    { element: 'Na', fx: 0, fy: 0, fz: 0, label: 'Na1' },
    { element: 'Na', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Na2' },
  ],
  metallic: true,
};

// Vanadium BCC — Im-3m, a = 3.0399 Å. Refractory transition metal;
// smaller-cell BCC counterpart to W.
export const VANADIUM_BCC: CrystalState = {
  name: 'Vanadium',
  lattice: { a: 3.0399, b: 3.0399, c: 3.0399, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'I m -3 m',
  basis: [
    { element: 'V', fx: 0, fy: 0, fz: 0, label: 'V1' },
    { element: 'V', fx: 0.5, fy: 0.5, fz: 0.5, label: 'V2' },
  ],
  metallic: true,
};

// Titanium HCP (α-Ti) — P6_3/mmc, a = 2.950, c = 4.686 Å. c/a ≈ 1.588
// — slightly below the ideal 1.633. Transforms to BCC (β-Ti) above ~882 °C.
export const TITANIUM_HCP: CrystalState = {
  name: 'Titanium',
  lattice: { a: 2.95, b: 2.95, c: 4.686, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63/m m c',
  basis: [
    { element: 'Ti', fx: 1 / 3, fy: 2 / 3, fz: 0.25, label: 'Ti1' },
    { element: 'Ti', fx: 2 / 3, fy: 1 / 3, fz: 0.75, label: 'Ti2' },
  ],
  metallic: true,
  conventionalCell: 'hexagonal-prism',
};

// Cobalt HCP (α-Co) — P6_3/mmc, a = 2.507, c = 4.069 Å. Ferromagnetic
// at room temperature; transforms to FCC above ~422 °C.
export const COBALT_HCP: CrystalState = {
  name: 'Cobalt',
  lattice: { a: 2.507, b: 2.507, c: 4.069, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63/m m c',
  basis: [
    { element: 'Co', fx: 1 / 3, fy: 2 / 3, fz: 0.25, label: 'Co1' },
    { element: 'Co', fx: 2 / 3, fy: 1 / 3, fz: 0.75, label: 'Co2' },
  ],
  metallic: true,
  conventionalCell: 'hexagonal-prism',
};

// Beryllium HCP — P6_3/mmc, a = 2.286, c = 3.583 Å. Lightest structural
// metal; very high specific stiffness. c/a ≈ 1.567 (below ideal).
export const BERYLLIUM_HCP: CrystalState = {
  name: 'Beryllium',
  lattice: { a: 2.286, b: 2.286, c: 3.583, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63/m m c',
  basis: [
    { element: 'Be', fx: 1 / 3, fy: 2 / 3, fz: 0.25, label: 'Be1' },
    { element: 'Be', fx: 2 / 3, fy: 1 / 3, fz: 0.75, label: 'Be2' },
  ],
  metallic: true,
  conventionalCell: 'hexagonal-prism',
};

// Potassium Chloride — rocksalt, a = 6.2917 Å. Larger alkali halide
// than NaCl; common ionic reference paired with LiF/NaCl/CsCl across the
// alkali series.
export const POTASSIUM_CHLORIDE: CrystalState = {
  name: 'Potassium Chloride',
  lattice: { a: 6.2917, b: 6.2917, c: 6.2917, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'K', fx: 0, fy: 0, fz: 0, label: 'K1' },
    { element: 'K', fx: 0, fy: 0.5, fz: 0.5, label: 'K2' },
    { element: 'K', fx: 0.5, fy: 0, fz: 0.5, label: 'K3' },
    { element: 'K', fx: 0.5, fy: 0.5, fz: 0, label: 'K4' },
    { element: 'Cl', fx: 0.5, fy: 0.5, fz: 0.5, label: 'Cl1' },
    { element: 'Cl', fx: 0.5, fy: 0, fz: 0, label: 'Cl2' },
    { element: 'Cl', fx: 0, fy: 0.5, fz: 0, label: 'Cl3' },
    { element: 'Cl', fx: 0, fy: 0, fz: 0.5, label: 'Cl4' },
  ],
  elementCharges: { K: 1, Cl: -1 },
};

// Silicon Carbide (β-SiC, 3C polytype) — zincblende structure with
// alternating Si and C, a = 4.3596 Å. Wide-bandgap semiconductor; the
// cubic polytype is the simplest of the many SiC stacking variants.
export const SILICON_CARBIDE: CrystalState = {
  name: 'Silicon Carbide',
  lattice: { a: 4.3596, b: 4.3596, c: 4.3596, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Si', fx: 0, fy: 0, fz: 0, label: 'Si1' },
    { element: 'Si', fx: 0, fy: 0.5, fz: 0.5, label: 'Si2' },
    { element: 'Si', fx: 0.5, fy: 0, fz: 0.5, label: 'Si3' },
    { element: 'Si', fx: 0.5, fy: 0.5, fz: 0, label: 'Si4' },
    { element: 'C', fx: 0.25, fy: 0.25, fz: 0.25, label: 'C1' },
    { element: 'C', fx: 0.25, fy: 0.75, fz: 0.75, label: 'C2' },
    { element: 'C', fx: 0.75, fy: 0.25, fz: 0.75, label: 'C3' },
    { element: 'C', fx: 0.75, fy: 0.75, fz: 0.25, label: 'C4' },
  ],
};

// Aluminum Nitride — wurtzite, a = 3.112, c = 4.982 Å. Wide-bandgap
// III-V; piezoelectric and thermally conductive, sister to GaN/ZnO at
// shorter bond lengths. u(N) ≈ 0.382.
export const ALUMINUM_NITRIDE: CrystalState = {
  name: 'Aluminum Nitride',
  lattice: { a: 3.112, b: 3.112, c: 4.982, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63 m c',
  basis: [
    { element: 'Al', fx: 1 / 3, fy: 2 / 3, fz: 0, label: 'Al1' },
    { element: 'Al', fx: 2 / 3, fy: 1 / 3, fz: 0.5, label: 'Al2' },
    { element: 'N', fx: 1 / 3, fy: 2 / 3, fz: 0.382, label: 'N1' },
    { element: 'N', fx: 2 / 3, fy: 1 / 3, fz: 0.882, label: 'N2' },
  ],
};

// Dry Ice (CO₂) — Pa-3 with C at FCC sites and O along body diagonals;
// each CO₂ molecule sits along a [111] direction with O-C-O ≈ 1.15 Å.
// a = 5.624 Å. All positions shifted by (¼, ¼, ¼) from their textbook
// Pa-3 values so each CO₂ lives in one cell octant — the literal-
// cartesian bond detection would otherwise miss the O atoms that wrap
// across the cell boundary.
export const DRY_ICE_CO2: CrystalState = {
  name: 'Dry Ice',
  lattice: { a: 5.624, b: 5.624, c: 5.624, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P a -3',
  basis: [
    { element: 'C', fx: 0.25, fy: 0.25, fz: 0.25, label: 'C1' },
    { element: 'C', fx: 0.25, fy: 0.75, fz: 0.75, label: 'C2' },
    { element: 'C', fx: 0.75, fy: 0.25, fz: 0.75, label: 'C3' },
    { element: 'C', fx: 0.75, fy: 0.75, fz: 0.25, label: 'C4' },
    { element: 'O', fx: 0.368, fy: 0.368, fz: 0.368, label: 'O1' },
    { element: 'O', fx: 0.132, fy: 0.132, fz: 0.132, label: 'O2' },
    { element: 'O', fx: 0.132, fy: 0.868, fz: 0.632, label: 'O3' },
    { element: 'O', fx: 0.368, fy: 0.632, fz: 0.868, label: 'O4' },
    { element: 'O', fx: 0.868, fy: 0.368, fz: 0.632, label: 'O5' },
    { element: 'O', fx: 0.632, fy: 0.132, fz: 0.868, label: 'O6' },
    { element: 'O', fx: 0.632, fy: 0.868, fz: 0.368, label: 'O7' },
    { element: 'O', fx: 0.868, fy: 0.632, fz: 0.132, label: 'O8' },
  ],
  molecular: true,
};

// Solid Nitrogen (α-N₂) — Pa-3, a = 5.661 Å. 4 N₂ molecules at FCC sites,
// each pointing along a different [111]-type direction. x ≈ 0.0567 gives
// N-N ≈ 1.11 Å, matching the triple-bond gas-phase length. All atoms
// shifted by (¼, ¼, ¼) from their textbook 8c values so each molecule
// lives in one cell octant — the literal-cartesian bond detection would
// otherwise miss the N pairs that wrap across the cell boundary.
export const NITROGEN_N2: CrystalState = {
  name: 'Nitrogen',
  lattice: { a: 5.661, b: 5.661, c: 5.661, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P a -3',
  basis: [
    { element: 'N', fx: 0.3067, fy: 0.3067, fz: 0.3067, label: 'N1' },
    { element: 'N', fx: 0.1933, fy: 0.1933, fz: 0.1933, label: 'N2' },
    { element: 'N', fx: 0.6933, fy: 0.1933, fz: 0.8067, label: 'N3' },
    { element: 'N', fx: 0.8067, fy: 0.3067, fz: 0.6933, label: 'N4' },
    { element: 'N', fx: 0.1933, fy: 0.8067, fz: 0.6933, label: 'N5' },
    { element: 'N', fx: 0.3067, fy: 0.6933, fz: 0.8067, label: 'N6' },
    { element: 'N', fx: 0.8067, fy: 0.6933, fz: 0.1933, label: 'N7' },
    { element: 'N', fx: 0.6933, fy: 0.8067, fz: 0.3067, label: 'N8' },
  ],
  molecular: true,
};

// Solid Argon — FCC Fm-3m, a = 5.311 Å at 4 K. Pure van der Waals crystal
// of monatomic Ar; no bonds (Ar-Ar at ~3.76 Å is far above any meaningful
// bonding cutoff). Categorized as molecular (rather than metallic)
// because the cohesion is dispersion-driven.
export const SOLID_ARGON: CrystalState = {
  name: 'Argon',
  lattice: { a: 5.311, b: 5.311, c: 5.311, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Ar', fx: 0, fy: 0, fz: 0, label: 'Ar1' },
    { element: 'Ar', fx: 0, fy: 0.5, fz: 0.5, label: 'Ar2' },
    { element: 'Ar', fx: 0.5, fy: 0, fz: 0.5, label: 'Ar3' },
    { element: 'Ar', fx: 0.5, fy: 0.5, fz: 0, label: 'Ar4' },
  ],
  molecular: true,
};

// Ice Ih (H₂O) — hexagonal P6_3/mmc, a = 4.5181, c = 7.356 Å. Real ice is
// proton-disordered (each H-bond's H sits on one of two positions with
// 50:50 occupancy); H atoms here are placed in ONE Bernal-Fowler-rules
// configuration so each cell renders as 4 discrete H₂O molecules. Each
// of the 8 H-bonds per cell carries exactly one H, near one of the two
// O ends. Donor directions are chosen so no two donors land on the same
// bond from opposite ends (a naive "all O₁/O₂ donate up + 1-down, all
// O₃/O₄ donate via the two same up-in-plane directions" rule placed
// two H atoms on the same bond ~0.85 Å apart, which the distance-cutoff
// bond detector then drew as a spurious H-H bond between molecules).
//
// The valid assignment used here:
//   O1 (z=0.0625) → +c (to O4) + 1 SE-down (to O3 below)
//   O2 (z=0.5625) → +c (to O3) + 1 S-down  (to O4 below)
//   O3 (z=0.9375) → 2 in-plane up: NE + S   (to two O1 images above)
//   O4 (z=0.4375) → 2 in-plane up: SE + SW  (to two O2 images above)
// Each of the 4 in-plane bond types is covered exactly once.
// Ice — cubic ice (ice Ic), generated by buildCubicIce() so the proton
// arrangement PROVABLY obeys the Bernal-Fowler ice rules. A hand-typed
// hexagonal-ice basis used to live here; getting 8 hydrogens placed by
// hand to satisfy the ice rules across periodic images is error-prone,
// and a structure that violates them is not at the force field's
// energy minimum — MD then relaxes it away from crystalline. The
// programmatic builder removes that whole class of bug. (The export is
// still named ICE_IH for continuity; the structure it holds is ice Ic.)
export const ICE_IH: CrystalState = buildCubicIce();

// Solid Methane (CH₄) — Phase I FCC at low T (rotationally disordered in
// reality). Modeled here as 4 C atoms in an FCC arrangement shifted by
// (¼, ¼, ¼) so each CH₄ tetrahedron lives in one octant of the cell —
// without the shift, half the H atoms wrap to the opposite side and the
// renderer (which uses literal cartesian distance, not minimum-image)
// shows broken CH₂-looking fragments. C-H = 1.087 Å, a = 5.892 Å.
export const SOLID_METHANE: CrystalState = {
  name: 'Methane',
  lattice: { a: 5.892, b: 5.892, c: 5.892, alpha: 90, beta: 90, gamma: 90 },
  // F-43m, not Fm-3m: the molecules sit at (¼,¼,¼)-type sites so each one
  // lies whole inside the cell, and in that setting the symmetry the
  // Crystal Editor's Fill cell applies must be F-43m — "Fm-3m" doubled the
  // molecules into the empty tetrahedral holes.
  spaceGroup: 'F -4 3 m',
  basis: [
    // C at FCC sites shifted by (¼, ¼, ¼)
    { element: 'C', fx: 0.25, fy: 0.25, fz: 0.25, label: 'C1' },
    { element: 'C', fx: 0.25, fy: 0.75, fz: 0.75, label: 'C2' },
    { element: 'C', fx: 0.75, fy: 0.25, fz: 0.75, label: 'C3' },
    { element: 'C', fx: 0.75, fy: 0.75, fz: 0.25, label: 'C4' },
    // 4 H around C1 along tetrahedral directions (offset ~0.1065 frac)
    { element: 'H', fx: 0.3565, fy: 0.3565, fz: 0.3565, label: 'H1' },
    { element: 'H', fx: 0.3565, fy: 0.1435, fz: 0.1435, label: 'H2' },
    { element: 'H', fx: 0.1435, fy: 0.3565, fz: 0.1435, label: 'H3' },
    { element: 'H', fx: 0.1435, fy: 0.1435, fz: 0.3565, label: 'H4' },
    // 4 H around C2
    { element: 'H', fx: 0.3565, fy: 0.8565, fz: 0.8565, label: 'H5' },
    { element: 'H', fx: 0.3565, fy: 0.6435, fz: 0.6435, label: 'H6' },
    { element: 'H', fx: 0.1435, fy: 0.8565, fz: 0.6435, label: 'H7' },
    { element: 'H', fx: 0.1435, fy: 0.6435, fz: 0.8565, label: 'H8' },
    // 4 H around C3
    { element: 'H', fx: 0.8565, fy: 0.3565, fz: 0.8565, label: 'H9' },
    { element: 'H', fx: 0.8565, fy: 0.1435, fz: 0.6435, label: 'H10' },
    { element: 'H', fx: 0.6435, fy: 0.3565, fz: 0.6435, label: 'H11' },
    { element: 'H', fx: 0.6435, fy: 0.1435, fz: 0.8565, label: 'H12' },
    // 4 H around C4
    { element: 'H', fx: 0.8565, fy: 0.8565, fz: 0.3565, label: 'H13' },
    { element: 'H', fx: 0.8565, fy: 0.6435, fz: 0.1435, label: 'H14' },
    { element: 'H', fx: 0.6435, fy: 0.8565, fz: 0.1435, label: 'H15' },
    { element: 'H', fx: 0.6435, fy: 0.6435, fz: 0.3565, label: 'H16' },
  ],
  molecular: true,
};

// Solid Hydrogen (H₂) — HCP P6_3/mmc, a = 3.776, c = 6.162 Å at 13.8 K.
// H₂ molecules centered at 2c Wyckoff sites with their axes along c
// (idealized — real solid H₂ has quantum-mechanical rotational disorder).
// H-H bond = 0.74 Å. Smallest molecular crystal by atom count: 4 H atoms.
export const SOLID_HYDROGEN: CrystalState = {
  name: 'Hydrogen',
  lattice: { a: 3.776, b: 3.776, c: 6.162, alpha: 90, beta: 90, gamma: 120 },
  spaceGroup: 'P 63/m m c',
  basis: [
    // H₂ molecule 1 centered at (1/3, 2/3, 1/4), axis along c
    { element: 'H', fx: 1 / 3, fy: 2 / 3, fz: 0.19, label: 'H1' },
    { element: 'H', fx: 1 / 3, fy: 2 / 3, fz: 0.31, label: 'H2' },
    // H₂ molecule 2 centered at (2/3, 1/3, 3/4)
    { element: 'H', fx: 2 / 3, fy: 1 / 3, fz: 0.69, label: 'H3' },
    { element: 'H', fx: 2 / 3, fy: 1 / 3, fz: 0.81, label: 'H4' },
  ],
  molecular: true,
};

// Solid Carbon Monoxide (α-CO) — P2_13, a = 5.63 Å at 19 K. Isostructural
// with α-N₂ as a Pa-3-like arrangement, but heteronuclear: each CO
// molecule points along a body diagonal with C at one end and O at the
// other. Real α-CO has head-tail orientational disorder (50:50 CO vs OC);
// this is one of the two ordered configurations. C-O ≈ 1.13 Å. C atoms
// shifted by (¼, ¼, ¼) from FCC corners so each molecule lives in one
// cell octant (avoids the boundary-wrap that breaks bond rendering).
export const SOLID_CO: CrystalState = {
  name: 'Carbon Monoxide',
  lattice: { a: 5.63, b: 5.63, c: 5.63, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P 21 3',
  basis: [
    { element: 'C', fx: 0.25, fy: 0.25, fz: 0.25, label: 'C1' },
    { element: 'C', fx: 0.75, fy: 0.25, fz: 0.75, label: 'C2' },
    { element: 'C', fx: 0.25, fy: 0.75, fz: 0.75, label: 'C3' },
    { element: 'C', fx: 0.75, fy: 0.75, fz: 0.25, label: 'C4' },
    // Each O sits 0.116 along the molecular [111] axis from its C partner.
    { element: 'O', fx: 0.366, fy: 0.366, fz: 0.366, label: 'O1' },
    { element: 'O', fx: 0.634, fy: 0.134, fz: 0.866, label: 'O2' },
    { element: 'O', fx: 0.134, fy: 0.866, fz: 0.634, label: 'O3' },
    { element: 'O', fx: 0.866, fy: 0.634, fz: 0.134, label: 'O4' },
  ],
  molecular: true,
};

// Solid Krypton — FCC Fm-3m, a = 5.706 Å at 4 K. Heavier noble-gas
// analog of Ar. Same monatomic van der Waals story, larger atomic radius.
export const SOLID_KRYPTON: CrystalState = {
  name: 'Krypton',
  lattice: { a: 5.706, b: 5.706, c: 5.706, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Kr', fx: 0, fy: 0, fz: 0, label: 'Kr1' },
    { element: 'Kr', fx: 0, fy: 0.5, fz: 0.5, label: 'Kr2' },
    { element: 'Kr', fx: 0.5, fy: 0, fz: 0.5, label: 'Kr3' },
    { element: 'Kr', fx: 0.5, fy: 0.5, fz: 0, label: 'Kr4' },
  ],
  molecular: true,
};

// Solid Xenon — FCC Fm-3m, a = 6.197 Å at 4 K. Largest stable noble gas;
// completes the Ne / Ar / Kr / Xe series of monatomic FCC crystals.
export const SOLID_XENON: CrystalState = {
  name: 'Xenon',
  lattice: { a: 6.197, b: 6.197, c: 6.197, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Xe', fx: 0, fy: 0, fz: 0, label: 'Xe1' },
    { element: 'Xe', fx: 0, fy: 0.5, fz: 0.5, label: 'Xe2' },
    { element: 'Xe', fx: 0.5, fy: 0, fz: 0.5, label: 'Xe3' },
    { element: 'Xe', fx: 0.5, fy: 0.5, fz: 0, label: 'Xe4' },
  ],
  molecular: true,
};

// Solid Neon — FCC Fm-3m, a = 4.466 Å at 4 K. Smallest stable noble-gas
// FCC crystal; completes the Ne / Ar / Kr / Xe series.
export const SOLID_NEON: CrystalState = {
  name: 'Neon',
  lattice: { a: 4.466, b: 4.466, c: 4.466, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'F m -3 m',
  basis: [
    { element: 'Ne', fx: 0, fy: 0, fz: 0, label: 'Ne1' },
    { element: 'Ne', fx: 0, fy: 0.5, fz: 0.5, label: 'Ne2' },
    { element: 'Ne', fx: 0.5, fy: 0, fz: 0.5, label: 'Ne3' },
    { element: 'Ne', fx: 0.5, fy: 0.5, fz: 0, label: 'Ne4' },
  ],
  molecular: true,
};

// Solid Ammonia (NH₃) — cubic; real ordered phase is P2_13 (a = 5.073 Å)
// where each N's lone pair points along a different [111] direction.
// Modeled here with all 4 NH₃ molecules in the SAME orientation (lone
// pair along +[111] for each), N at FCC sites shifted by (¼, ¼, ¼) so
// each pyramidal NH₃ sits in a cell octant — without the shift, half the
// H atoms wrap to the opposite side and bonds disappear. N-H ≈ 1.01 Å.
export const SOLID_AMMONIA: CrystalState = {
  name: 'Ammonia',
  lattice: { a: 5.073, b: 5.073, c: 5.073, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P 1',
  basis: [
    { element: 'N', fx: 0.25, fy: 0.25, fz: 0.25, label: 'N1' },
    { element: 'N', fx: 0.75, fy: 0.25, fz: 0.75, label: 'N2' },
    { element: 'N', fx: 0.25, fy: 0.75, fz: 0.75, label: 'N3' },
    { element: 'N', fx: 0.75, fy: 0.75, fz: 0.25, label: 'N4' },
    // 3 H around N1 (lone pair along +[111]; H along (1,-1,-1), (-1,1,-1), (-1,-1,1))
    { element: 'H', fx: 0.365, fy: 0.135, fz: 0.135, label: 'H1' },
    { element: 'H', fx: 0.135, fy: 0.365, fz: 0.135, label: 'H2' },
    { element: 'H', fx: 0.135, fy: 0.135, fz: 0.365, label: 'H3' },
    // 3 H around N2
    { element: 'H', fx: 0.865, fy: 0.135, fz: 0.635, label: 'H4' },
    { element: 'H', fx: 0.635, fy: 0.365, fz: 0.635, label: 'H5' },
    { element: 'H', fx: 0.635, fy: 0.135, fz: 0.865, label: 'H6' },
    // 3 H around N3
    { element: 'H', fx: 0.365, fy: 0.635, fz: 0.635, label: 'H7' },
    { element: 'H', fx: 0.135, fy: 0.865, fz: 0.635, label: 'H8' },
    { element: 'H', fx: 0.135, fy: 0.635, fz: 0.865, label: 'H9' },
    // 3 H around N4
    { element: 'H', fx: 0.865, fy: 0.635, fz: 0.135, label: 'H10' },
    { element: 'H', fx: 0.635, fy: 0.865, fz: 0.135, label: 'H11' },
    { element: 'H', fx: 0.6435, fy: 0.6435, fz: 0.3565, label: 'H16' },
  ],
  molecular: true,
};

// Sulfur Hexafluoride (SF₆) — high-T phase I is Im-3m with rotationally
// disordered molecules at BCC sites (a ≈ 5.81 Å). Rendered as a static
// snapshot: octahedral SF₆ at each BCC site with 6 F at the ±x/y/z
// vertices. S-F ≈ 1.564 Å. Classic example of an octahedral molecular
// crystal.
// Sulfur Hexafluoride (SF₆) — Fm-3m with 4 octahedral SF₆ molecules at
// shifted-FCC sites (the same layout pattern as dry ice, CH₄, CCl₄,
// CBr₄, …). Earlier versions used Im-3m at a = 5.81 Å with 2 SF₆/cell,
// but BCC packing puts F atoms at fractional 0.731 — past the cell
// midline — and the supercell builder doesn't connect bonds across
// the supercell's outer boundary, so 27 F atoms per 3³ supercell
// became orphans (no S bond, no moleculeName partner) and LJ-clashed
// with intramolecular F neighbors at 2.21 Å. Shifted-FCC at a = 7.32
// Å preserves the original molecular density (4 SF₆/cell ÷ a³ ≈
// 0.0102 mol/Å³, matching BCC's 2/5.81³) while keeping every F atom
// inside its parent's cell. S-F = 1.56 Å (rigid octahedron); F offset
// = 1.56/7.32 = 0.213 fractional, comfortably within [0.037, 0.463]
// for an S at (0.25, …) and [0.537, 0.963] for an S at (0.75, …).
export const SOLID_SF6: CrystalState = {
  name: 'Sulfur Hexafluoride',
  lattice: { a: 7.32, b: 7.32, c: 7.32, alpha: 90, beta: 90, gamma: 90 },
  // F-43m, not Fm-3m: the molecules sit at (¼,¼,¼)-type sites so each one
  // lies whole inside the cell, and in that setting the symmetry the
  // Crystal Editor's Fill cell applies must be F-43m — "Fm-3m" doubled the
  // molecules into the empty tetrahedral holes.
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'S', fx: 0.25, fy: 0.25, fz: 0.25, label: 'S1' },
    { element: 'S', fx: 0.25, fy: 0.75, fz: 0.75, label: 'S2' },
    { element: 'S', fx: 0.75, fy: 0.25, fz: 0.75, label: 'S3' },
    { element: 'S', fx: 0.75, fy: 0.75, fz: 0.25, label: 'S4' },
    // 6 F around S1 (octahedral, along ±x, ±y, ±z; offset 0.213)
    { element: 'F', fx: 0.463, fy: 0.25, fz: 0.25, label: 'F1' },
    { element: 'F', fx: 0.037, fy: 0.25, fz: 0.25, label: 'F2' },
    { element: 'F', fx: 0.25, fy: 0.463, fz: 0.25, label: 'F3' },
    { element: 'F', fx: 0.25, fy: 0.037, fz: 0.25, label: 'F4' },
    { element: 'F', fx: 0.25, fy: 0.25, fz: 0.463, label: 'F5' },
    { element: 'F', fx: 0.25, fy: 0.25, fz: 0.037, label: 'F6' },
    // 6 F around S2
    { element: 'F', fx: 0.463, fy: 0.75, fz: 0.75, label: 'F7' },
    { element: 'F', fx: 0.037, fy: 0.75, fz: 0.75, label: 'F8' },
    { element: 'F', fx: 0.25, fy: 0.963, fz: 0.75, label: 'F9' },
    { element: 'F', fx: 0.25, fy: 0.537, fz: 0.75, label: 'F10' },
    { element: 'F', fx: 0.25, fy: 0.75, fz: 0.963, label: 'F11' },
    { element: 'F', fx: 0.25, fy: 0.75, fz: 0.537, label: 'F12' },
    // 6 F around S3
    { element: 'F', fx: 0.963, fy: 0.25, fz: 0.75, label: 'F13' },
    { element: 'F', fx: 0.537, fy: 0.25, fz: 0.75, label: 'F14' },
    { element: 'F', fx: 0.75, fy: 0.463, fz: 0.75, label: 'F15' },
    { element: 'F', fx: 0.75, fy: 0.037, fz: 0.75, label: 'F16' },
    { element: 'F', fx: 0.75, fy: 0.25, fz: 0.963, label: 'F17' },
    { element: 'F', fx: 0.75, fy: 0.25, fz: 0.537, label: 'F18' },
    // 6 F around S4
    { element: 'F', fx: 0.963, fy: 0.75, fz: 0.25, label: 'F19' },
    { element: 'F', fx: 0.537, fy: 0.75, fz: 0.25, label: 'F20' },
    { element: 'F', fx: 0.75, fy: 0.963, fz: 0.25, label: 'F21' },
    { element: 'F', fx: 0.75, fy: 0.537, fz: 0.25, label: 'F22' },
    { element: 'F', fx: 0.75, fy: 0.75, fz: 0.463, label: 'F23' },
    { element: 'F', fx: 0.75, fy: 0.75, fz: 0.037, label: 'F24' },
  ],
  molecular: true,
};

// Solid Acetylene (C₂H₂) — high-T cubic phase (Pa-3, a ≈ 6.10 Å, stable
// ~133-192 K). 4 linear H-C≡C-H molecules per cell, each along a [111]
// body diagonal. C≡C = 1.20 Å, C-H = 1.06 Å. All atoms shifted by
// (¼, ¼, ¼) from textbook Pa-3 so each molecule lives in one cell
// octant.
export const SOLID_ACETYLENE: CrystalState = {
  name: 'Acetylene',
  lattice: { a: 6.1, b: 6.1, c: 6.1, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P a -3',
  basis: [
    // mol1 along (1,1,1): H-C-C-H
    { element: 'H', fx: 0.4071, fy: 0.4071, fz: 0.4071, label: 'H1' },
    { element: 'C', fx: 0.3068, fy: 0.3068, fz: 0.3068, label: 'C1' },
    { element: 'C', fx: 0.1932, fy: 0.1932, fz: 0.1932, label: 'C2' },
    { element: 'H', fx: 0.0929, fy: 0.0929, fz: 0.0929, label: 'H2' },
    // mol2 along (1,1,-1)
    { element: 'H', fx: 0.9071, fy: 0.4071, fz: 0.5929, label: 'H3' },
    { element: 'C', fx: 0.8067, fy: 0.3067, fz: 0.6933, label: 'C3' },
    { element: 'C', fx: 0.6933, fy: 0.1933, fz: 0.8067, label: 'C4' },
    { element: 'H', fx: 0.5929, fy: 0.0929, fz: 0.9071, label: 'H4' },
    // mol3 along (-1,1,-1)
    { element: 'H', fx: 0.0929, fy: 0.9071, fz: 0.5929, label: 'H5' },
    { element: 'C', fx: 0.1933, fy: 0.8067, fz: 0.6933, label: 'C5' },
    { element: 'C', fx: 0.3067, fy: 0.6933, fz: 0.8067, label: 'C6' },
    { element: 'H', fx: 0.4071, fy: 0.5929, fz: 0.9071, label: 'H6' },
    // mol4 along (1,-1,-1)
    { element: 'H', fx: 0.9071, fy: 0.5929, fz: 0.0929, label: 'H7' },
    { element: 'C', fx: 0.8067, fy: 0.6933, fz: 0.1933, label: 'C7' },
    { element: 'C', fx: 0.6933, fy: 0.8067, fz: 0.3067, label: 'C8' },
    { element: 'H', fx: 0.5929, fy: 0.9071, fz: 0.4071, label: 'H8' },
  ],
  molecular: true,
};

// Carbon Tetrafluoride (CF₄) — FCC plastic crystal at higher T,
// rotationally disordered in reality. Modeled as 4 tetrahedral CF₄
// molecules with C at shifted FCC sites and 4 F per C at tetrahedral
// vertices. C-F = 1.32 Å, a = 7.4 Å.
export const SOLID_CF4: CrystalState = {
  name: 'Carbon Tetrafluoride',
  lattice: { a: 7.4, b: 7.4, c: 7.4, alpha: 90, beta: 90, gamma: 90 },
  // F-43m, not Fm-3m: the molecules sit at (¼,¼,¼)-type sites so each one
  // lies whole inside the cell, and in that setting the symmetry the
  // Crystal Editor's Fill cell applies must be F-43m — "Fm-3m" doubled the
  // molecules into the empty tetrahedral holes.
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'C', fx: 0.25, fy: 0.25, fz: 0.25, label: 'C1' },
    { element: 'C', fx: 0.25, fy: 0.75, fz: 0.75, label: 'C2' },
    { element: 'C', fx: 0.75, fy: 0.25, fz: 0.75, label: 'C3' },
    { element: 'C', fx: 0.75, fy: 0.75, fz: 0.25, label: 'C4' },
    // 4 F around C1
    { element: 'F', fx: 0.353, fy: 0.353, fz: 0.353, label: 'F1' },
    { element: 'F', fx: 0.353, fy: 0.147, fz: 0.147, label: 'F2' },
    { element: 'F', fx: 0.147, fy: 0.353, fz: 0.147, label: 'F3' },
    { element: 'F', fx: 0.147, fy: 0.147, fz: 0.353, label: 'F4' },
    // 4 F around C2
    { element: 'F', fx: 0.353, fy: 0.853, fz: 0.853, label: 'F5' },
    { element: 'F', fx: 0.353, fy: 0.647, fz: 0.647, label: 'F6' },
    { element: 'F', fx: 0.147, fy: 0.853, fz: 0.647, label: 'F7' },
    { element: 'F', fx: 0.147, fy: 0.647, fz: 0.853, label: 'F8' },
    // 4 F around C3
    { element: 'F', fx: 0.853, fy: 0.353, fz: 0.853, label: 'F9' },
    { element: 'F', fx: 0.853, fy: 0.147, fz: 0.647, label: 'F10' },
    { element: 'F', fx: 0.647, fy: 0.353, fz: 0.647, label: 'F11' },
    { element: 'F', fx: 0.647, fy: 0.147, fz: 0.853, label: 'F12' },
    // 4 F around C4
    { element: 'F', fx: 0.853, fy: 0.853, fz: 0.353, label: 'F13' },
    { element: 'F', fx: 0.853, fy: 0.647, fz: 0.147, label: 'F14' },
    { element: 'F', fx: 0.647, fy: 0.853, fz: 0.147, label: 'F15' },
    { element: 'F', fx: 0.647, fy: 0.647, fz: 0.353, label: 'F16' },
  ],
  molecular: true,
};

// Carbon Tetrachloride (CCl₄) — FCC plastic crystal phase (high-T
// rotationally disordered in reality). Modeled as 4 tetrahedral CCl₄
// molecules with C at shifted FCC sites and 4 Cl per C at tetrahedral
// vertices. C-Cl = 1.77 Å, a = 8.32 Å.
export const SOLID_CCL4: CrystalState = {
  name: 'Carbon Tetrachloride',
  lattice: { a: 8.32, b: 8.32, c: 8.32, alpha: 90, beta: 90, gamma: 90 },
  // F-43m, not Fm-3m: the molecules sit at (¼,¼,¼)-type sites so each one
  // lies whole inside the cell, and in that setting the symmetry the
  // Crystal Editor's Fill cell applies must be F-43m — "Fm-3m" doubled the
  // molecules into the empty tetrahedral holes.
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'C', fx: 0.25, fy: 0.25, fz: 0.25, label: 'C1' },
    { element: 'C', fx: 0.25, fy: 0.75, fz: 0.75, label: 'C2' },
    { element: 'C', fx: 0.75, fy: 0.25, fz: 0.75, label: 'C3' },
    { element: 'C', fx: 0.75, fy: 0.75, fz: 0.25, label: 'C4' },
    // 4 Cl around C1 (offset 0.123 along each tetrahedral axis component)
    { element: 'Cl', fx: 0.373, fy: 0.373, fz: 0.373, label: 'Cl1' },
    { element: 'Cl', fx: 0.373, fy: 0.127, fz: 0.127, label: 'Cl2' },
    { element: 'Cl', fx: 0.127, fy: 0.373, fz: 0.127, label: 'Cl3' },
    { element: 'Cl', fx: 0.127, fy: 0.127, fz: 0.373, label: 'Cl4' },
    // 4 Cl around C2
    { element: 'Cl', fx: 0.373, fy: 0.873, fz: 0.873, label: 'Cl5' },
    { element: 'Cl', fx: 0.373, fy: 0.627, fz: 0.627, label: 'Cl6' },
    { element: 'Cl', fx: 0.127, fy: 0.873, fz: 0.627, label: 'Cl7' },
    { element: 'Cl', fx: 0.127, fy: 0.627, fz: 0.873, label: 'Cl8' },
    // 4 Cl around C3
    { element: 'Cl', fx: 0.873, fy: 0.373, fz: 0.873, label: 'Cl9' },
    { element: 'Cl', fx: 0.873, fy: 0.127, fz: 0.627, label: 'Cl10' },
    { element: 'Cl', fx: 0.627, fy: 0.373, fz: 0.627, label: 'Cl11' },
    { element: 'Cl', fx: 0.627, fy: 0.127, fz: 0.873, label: 'Cl12' },
    // 4 Cl around C4
    { element: 'Cl', fx: 0.873, fy: 0.873, fz: 0.373, label: 'Cl13' },
    { element: 'Cl', fx: 0.873, fy: 0.627, fz: 0.127, label: 'Cl14' },
    { element: 'Cl', fx: 0.627, fy: 0.873, fz: 0.127, label: 'Cl15' },
    { element: 'Cl', fx: 0.627, fy: 0.627, fz: 0.373, label: 'Cl16' },
  ],
  molecular: true,
};

// Nitrous Oxide (N₂O) — P2_13 (non-centrosymmetric subgroup of Pa-3),
// a ≈ 5.65 Å. 4 asymmetric linear N=N=O molecules at FCC sites, each
// along a [111]-type body diagonal. N-N ≈ 1.13 Å, N-O ≈ 1.18 Å. C-style
// shift (¼, ¼, ¼) so each molecule lives in one cell octant.
export const SOLID_N2O: CrystalState = {
  name: 'Nitrous Oxide',
  lattice: { a: 5.65, b: 5.65, c: 5.65, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P 21 3',
  basis: [
    // mol1 along (1,1,1): O - N_center - N_end (along +axis)
    { element: 'O', fx: 0.129, fy: 0.129, fz: 0.129, label: 'O1' },
    { element: 'N', fx: 0.25, fy: 0.25, fz: 0.25, label: 'N1' },
    { element: 'N', fx: 0.366, fy: 0.366, fz: 0.366, label: 'N2' },
    // mol2 along (-1,-1,1)
    { element: 'O', fx: 0.371, fy: 0.871, fz: 0.629, label: 'O2' },
    { element: 'N', fx: 0.25, fy: 0.75, fz: 0.75, label: 'N3' },
    { element: 'N', fx: 0.135, fy: 0.634, fz: 0.866, label: 'N4' },
    // mol3 along (-1,1,-1)
    { element: 'O', fx: 0.871, fy: 0.629, fz: 0.371, label: 'O3' },
    { element: 'N', fx: 0.75, fy: 0.75, fz: 0.25, label: 'N5' },
    { element: 'N', fx: 0.634, fy: 0.866, fz: 0.135, label: 'N6' },
    // mol4 along (1,-1,-1)
    { element: 'O', fx: 0.629, fy: 0.371, fz: 0.871, label: 'O4' },
    { element: 'N', fx: 0.75, fy: 0.25, fz: 0.75, label: 'N7' },
    { element: 'N', fx: 0.866, fy: 0.135, fz: 0.634, label: 'N8' },
  ],
  molecular: true,
};

// Hydrogen Cyanide (HCN) — modeled in idealized P2_13 (real low-T HCN is
// orthorhombic; this is a pedagogical "Pa-3-like" view). 4 linear
// H-C≡N molecules at FCC sites along [111]-type body diagonals.
// H-C = 1.07 Å, C≡N = 1.16 Å. a = 5.5 Å.
export const SOLID_HCN: CrystalState = {
  name: 'Hydrogen Cyanide',
  lattice: { a: 5.5, b: 5.5, c: 5.5, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P 21 3',
  basis: [
    // mol1 along (1,1,1): H - C - N
    { element: 'H', fx: 0.138, fy: 0.138, fz: 0.138, label: 'H1' },
    { element: 'C', fx: 0.25, fy: 0.25, fz: 0.25, label: 'C1' },
    { element: 'N', fx: 0.372, fy: 0.372, fz: 0.372, label: 'N1' },
    // mol2 along (-1,-1,1)
    { element: 'H', fx: 0.362, fy: 0.862, fz: 0.638, label: 'H2' },
    { element: 'C', fx: 0.25, fy: 0.75, fz: 0.75, label: 'C2' },
    { element: 'N', fx: 0.128, fy: 0.628, fz: 0.872, label: 'N2' },
    // mol3 along (-1,1,-1)
    { element: 'H', fx: 0.862, fy: 0.638, fz: 0.362, label: 'H3' },
    { element: 'C', fx: 0.75, fy: 0.75, fz: 0.25, label: 'C3' },
    { element: 'N', fx: 0.628, fy: 0.872, fz: 0.128, label: 'N3' },
    // mol4 along (1,-1,-1)
    { element: 'H', fx: 0.638, fy: 0.362, fz: 0.862, label: 'H4' },
    { element: 'C', fx: 0.75, fy: 0.25, fz: 0.75, label: 'C4' },
    { element: 'N', fx: 0.872, fy: 0.128, fz: 0.628, label: 'N4' },
  ],
  molecular: true,
};

// White Phosphorus (α-P₄) — BCC-like arrangement of discrete P₄
// tetrahedra. Real α-P has a large 56-atom monoclinic cell at low T;
// this is a simplified BCC model with 2 P₄ tetrahedra per cell, one at
// each shifted BCC site. Tetrahedron edge P-P = 2.21 Å, a = 7.5 Å (chosen
// so the closest inter-tetrahedron P···P distance, ~3.80 Å, comfortably
// exceeds the P-P bonding cutoff of 2.53 Å — without that spacing, the
// distance-cutoff bond detector would draw spurious bonds between
// neighboring tetrahedra). Requires the P bonding-radius override in
// elementVisuals (library's 0.75 → 1.10).
export const WHITE_PHOSPHORUS: CrystalState = {
  name: 'White Phosphorus',
  lattice: { a: 7.5, b: 7.5, c: 7.5, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'I -4 3 m',
  basis: [
    // P₄ tetrahedron 1 centered at (0.25, 0.25, 0.25); vertices offset
    // 0.781 Å (= 2.21/(2√2)) along the four (±,±,±) tetrahedral
    // directions, in fractional coords that's ±0.104.
    { element: 'P', fx: 0.354, fy: 0.354, fz: 0.354, label: 'P1' },
    { element: 'P', fx: 0.354, fy: 0.146, fz: 0.146, label: 'P2' },
    { element: 'P', fx: 0.146, fy: 0.354, fz: 0.146, label: 'P3' },
    { element: 'P', fx: 0.146, fy: 0.146, fz: 0.354, label: 'P4' },
    // P₄ tetrahedron 2 centered at (0.75, 0.75, 0.75)
    { element: 'P', fx: 0.854, fy: 0.854, fz: 0.854, label: 'P5' },
    { element: 'P', fx: 0.854, fy: 0.646, fz: 0.646, label: 'P6' },
    { element: 'P', fx: 0.646, fy: 0.854, fz: 0.646, label: 'P7' },
    { element: 'P', fx: 0.646, fy: 0.646, fz: 0.854, label: 'P8' },
  ],
  molecular: true,
};

// Solid Cyanogen (C₂N₂) — Pa-3-like cubic with 4 linear N≡C-C≡N molecules
// along the four [111] body diagonals (the molecule is centrosymmetric,
// so Pa-3 works). C-C single = 1.39 Å, C≡N triple = 1.16 Å. a = 6.85 Å.
// Shifted by (¼, ¼, ¼) so each molecule lives in one cell octant.
export const SOLID_CYANOGEN: CrystalState = {
  name: 'Cyanogen',
  lattice: { a: 6.85, b: 6.85, c: 6.85, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P a -3',
  basis: [
    // mol1 along (1,1,1): N-C-C-N
    { element: 'N', fx: 0.4064, fy: 0.4064, fz: 0.4064, label: 'N1' },
    { element: 'C', fx: 0.3086, fy: 0.3086, fz: 0.3086, label: 'C1' },
    { element: 'C', fx: 0.1914, fy: 0.1914, fz: 0.1914, label: 'C2' },
    { element: 'N', fx: 0.0936, fy: 0.0936, fz: 0.0936, label: 'N2' },
    // mol2 along (1,1,-1)
    { element: 'N', fx: 0.9064, fy: 0.4064, fz: 0.5936, label: 'N3' },
    { element: 'C', fx: 0.8086, fy: 0.3086, fz: 0.6914, label: 'C3' },
    { element: 'C', fx: 0.6914, fy: 0.1914, fz: 0.8086, label: 'C4' },
    { element: 'N', fx: 0.5936, fy: 0.0936, fz: 0.9064, label: 'N4' },
    // mol3 along (-1,1,-1)
    { element: 'N', fx: 0.0936, fy: 0.9064, fz: 0.5936, label: 'N5' },
    { element: 'C', fx: 0.1914, fy: 0.8086, fz: 0.6914, label: 'C5' },
    { element: 'C', fx: 0.3086, fy: 0.6914, fz: 0.8086, label: 'C6' },
    { element: 'N', fx: 0.4064, fy: 0.5936, fz: 0.9064, label: 'N6' },
    // mol4 along (1,-1,-1)
    { element: 'N', fx: 0.9064, fy: 0.5936, fz: 0.0936, label: 'N7' },
    { element: 'C', fx: 0.8086, fy: 0.6914, fz: 0.1914, label: 'C7' },
    { element: 'C', fx: 0.6914, fy: 0.8086, fz: 0.3086, label: 'C8' },
    { element: 'N', fx: 0.5936, fy: 0.9064, fz: 0.4064, label: 'N8' },
  ],
  molecular: true,
};

// Silane (SiH₄) — Methane analog, FCC arrangement of tetrahedral SiH₄
// molecules. Si-H = 1.48 Å, a = 6.4 Å. Same shifted-FCC layout as CH₄.
export const SOLID_SILANE: CrystalState = {
  name: 'Silane',
  lattice: { a: 6.4, b: 6.4, c: 6.4, alpha: 90, beta: 90, gamma: 90 },
  // F-43m, not Fm-3m: the molecules sit at (¼,¼,¼)-type sites so each one
  // lies whole inside the cell, and in that setting the symmetry the
  // Crystal Editor's Fill cell applies must be F-43m — "Fm-3m" doubled the
  // molecules into the empty tetrahedral holes.
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Si', fx: 0.25, fy: 0.25, fz: 0.25, label: 'Si1' },
    { element: 'Si', fx: 0.25, fy: 0.75, fz: 0.75, label: 'Si2' },
    { element: 'Si', fx: 0.75, fy: 0.25, fz: 0.75, label: 'Si3' },
    { element: 'Si', fx: 0.75, fy: 0.75, fz: 0.25, label: 'Si4' },
    // 4 H around Si1
    { element: 'H', fx: 0.3835, fy: 0.3835, fz: 0.3835, label: 'H1' },
    { element: 'H', fx: 0.3835, fy: 0.1165, fz: 0.1165, label: 'H2' },
    { element: 'H', fx: 0.1165, fy: 0.3835, fz: 0.1165, label: 'H3' },
    { element: 'H', fx: 0.1165, fy: 0.1165, fz: 0.3835, label: 'H4' },
    // 4 H around Si2
    { element: 'H', fx: 0.3835, fy: 0.8835, fz: 0.8835, label: 'H5' },
    { element: 'H', fx: 0.3835, fy: 0.6165, fz: 0.6165, label: 'H6' },
    { element: 'H', fx: 0.1165, fy: 0.8835, fz: 0.6165, label: 'H7' },
    { element: 'H', fx: 0.1165, fy: 0.6165, fz: 0.8835, label: 'H8' },
    // 4 H around Si3
    { element: 'H', fx: 0.8835, fy: 0.3835, fz: 0.8835, label: 'H9' },
    { element: 'H', fx: 0.8835, fy: 0.1165, fz: 0.6165, label: 'H10' },
    { element: 'H', fx: 0.6165, fy: 0.3835, fz: 0.6165, label: 'H11' },
    { element: 'H', fx: 0.6165, fy: 0.1165, fz: 0.8835, label: 'H12' },
    // 4 H around Si4
    { element: 'H', fx: 0.8835, fy: 0.8835, fz: 0.3835, label: 'H13' },
    { element: 'H', fx: 0.8835, fy: 0.6165, fz: 0.1165, label: 'H14' },
    { element: 'H', fx: 0.6165, fy: 0.8835, fz: 0.1165, label: 'H15' },
    { element: 'H', fx: 0.6165, fy: 0.6165, fz: 0.3835, label: 'H16' },
  ],
  molecular: true,
};

// Phosphine (PH₃) — Ammonia analog with pyramidal PH₃ molecules at
// shifted FCC sites, all oriented identically (lone pair along +[111]).
// P-H = 1.42 Å, a = 5.8 Å. Real solid PH₃ has more complex low-T phases;
// this is a pedagogical FCC representation.
export const SOLID_PHOSPHINE: CrystalState = {
  name: 'Phosphine',
  lattice: { a: 5.8, b: 5.8, c: 5.8, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P 1',
  basis: [
    { element: 'P', fx: 0.25, fy: 0.25, fz: 0.25, label: 'P1' },
    { element: 'P', fx: 0.75, fy: 0.25, fz: 0.75, label: 'P2' },
    { element: 'P', fx: 0.25, fy: 0.75, fz: 0.75, label: 'P3' },
    { element: 'P', fx: 0.75, fy: 0.75, fz: 0.25, label: 'P4' },
    // 3 H around P1 (lone pair along +[111])
    { element: 'H', fx: 0.3914, fy: 0.1086, fz: 0.1086, label: 'H1' },
    { element: 'H', fx: 0.1086, fy: 0.3914, fz: 0.1086, label: 'H2' },
    { element: 'H', fx: 0.1086, fy: 0.1086, fz: 0.3914, label: 'H3' },
    // 3 H around P2
    { element: 'H', fx: 0.8914, fy: 0.1086, fz: 0.6086, label: 'H4' },
    { element: 'H', fx: 0.6086, fy: 0.3914, fz: 0.6086, label: 'H5' },
    { element: 'H', fx: 0.6086, fy: 0.1086, fz: 0.8914, label: 'H6' },
    // 3 H around P3
    { element: 'H', fx: 0.3914, fy: 0.6086, fz: 0.6086, label: 'H7' },
    { element: 'H', fx: 0.1086, fy: 0.8914, fz: 0.6086, label: 'H8' },
    { element: 'H', fx: 0.1086, fy: 0.6086, fz: 0.8914, label: 'H9' },
    // 3 H around P4
    { element: 'H', fx: 0.8914, fy: 0.6086, fz: 0.1086, label: 'H10' },
    { element: 'H', fx: 0.6086, fy: 0.8914, fz: 0.1086, label: 'H11' },
    { element: 'H', fx: 0.6086, fy: 0.6086, fz: 0.3914, label: 'H12' },
  ],
  molecular: true,
};

// Carbon Tetrabromide (CBr₄) — FCC plastic crystal (high-T rotationally
// disordered in reality). Modeled as 4 tetrahedral CBr₄ molecules with C
// at shifted FCC sites and 4 Br per C at tetrahedral vertices.
// C-Br = 1.94 Å, a = 8.82 Å.
export const SOLID_CBR4: CrystalState = {
  name: 'Carbon Tetrabromide',
  lattice: { a: 8.82, b: 8.82, c: 8.82, alpha: 90, beta: 90, gamma: 90 },
  // F-43m, not Fm-3m: the molecules sit at (¼,¼,¼)-type sites so each one
  // lies whole inside the cell, and in that setting the symmetry the
  // Crystal Editor's Fill cell applies must be F-43m — "Fm-3m" doubled the
  // molecules into the empty tetrahedral holes.
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'C', fx: 0.25, fy: 0.25, fz: 0.25, label: 'C1' },
    { element: 'C', fx: 0.25, fy: 0.75, fz: 0.75, label: 'C2' },
    { element: 'C', fx: 0.75, fy: 0.25, fz: 0.75, label: 'C3' },
    { element: 'C', fx: 0.75, fy: 0.75, fz: 0.25, label: 'C4' },
    { element: 'Br', fx: 0.377, fy: 0.377, fz: 0.377, label: 'Br1' },
    { element: 'Br', fx: 0.377, fy: 0.123, fz: 0.123, label: 'Br2' },
    { element: 'Br', fx: 0.123, fy: 0.377, fz: 0.123, label: 'Br3' },
    { element: 'Br', fx: 0.123, fy: 0.123, fz: 0.377, label: 'Br4' },
    { element: 'Br', fx: 0.377, fy: 0.877, fz: 0.877, label: 'Br5' },
    { element: 'Br', fx: 0.377, fy: 0.623, fz: 0.623, label: 'Br6' },
    { element: 'Br', fx: 0.123, fy: 0.877, fz: 0.623, label: 'Br7' },
    { element: 'Br', fx: 0.123, fy: 0.623, fz: 0.877, label: 'Br8' },
    { element: 'Br', fx: 0.877, fy: 0.377, fz: 0.877, label: 'Br9' },
    { element: 'Br', fx: 0.877, fy: 0.123, fz: 0.623, label: 'Br10' },
    { element: 'Br', fx: 0.623, fy: 0.377, fz: 0.623, label: 'Br11' },
    { element: 'Br', fx: 0.623, fy: 0.123, fz: 0.877, label: 'Br12' },
    { element: 'Br', fx: 0.877, fy: 0.877, fz: 0.377, label: 'Br13' },
    { element: 'Br', fx: 0.877, fy: 0.623, fz: 0.123, label: 'Br14' },
    { element: 'Br', fx: 0.623, fy: 0.877, fz: 0.123, label: 'Br15' },
    { element: 'Br', fx: 0.623, fy: 0.623, fz: 0.377, label: 'Br16' },
  ],
  molecular: true,
};

// Germane (GeH₄) — silane analog with Ge. FCC tetrahedral arrangement.
// Ge-H = 1.53 Å, a = 6.5 Å.
export const SOLID_GERMANE: CrystalState = {
  name: 'Germane',
  lattice: { a: 6.5, b: 6.5, c: 6.5, alpha: 90, beta: 90, gamma: 90 },
  // F-43m, not Fm-3m: the molecules sit at (¼,¼,¼)-type sites so each one
  // lies whole inside the cell, and in that setting the symmetry the
  // Crystal Editor's Fill cell applies must be F-43m — "Fm-3m" doubled the
  // molecules into the empty tetrahedral holes.
  spaceGroup: 'F -4 3 m',
  basis: [
    { element: 'Ge', fx: 0.25, fy: 0.25, fz: 0.25, label: 'Ge1' },
    { element: 'Ge', fx: 0.25, fy: 0.75, fz: 0.75, label: 'Ge2' },
    { element: 'Ge', fx: 0.75, fy: 0.25, fz: 0.75, label: 'Ge3' },
    { element: 'Ge', fx: 0.75, fy: 0.75, fz: 0.25, label: 'Ge4' },
    { element: 'H', fx: 0.386, fy: 0.386, fz: 0.386, label: 'H1' },
    { element: 'H', fx: 0.386, fy: 0.114, fz: 0.114, label: 'H2' },
    { element: 'H', fx: 0.114, fy: 0.386, fz: 0.114, label: 'H3' },
    { element: 'H', fx: 0.114, fy: 0.114, fz: 0.386, label: 'H4' },
    { element: 'H', fx: 0.386, fy: 0.886, fz: 0.886, label: 'H5' },
    { element: 'H', fx: 0.386, fy: 0.614, fz: 0.614, label: 'H6' },
    { element: 'H', fx: 0.114, fy: 0.886, fz: 0.614, label: 'H7' },
    { element: 'H', fx: 0.114, fy: 0.614, fz: 0.886, label: 'H8' },
    { element: 'H', fx: 0.886, fy: 0.386, fz: 0.886, label: 'H9' },
    { element: 'H', fx: 0.886, fy: 0.114, fz: 0.614, label: 'H10' },
    { element: 'H', fx: 0.614, fy: 0.386, fz: 0.614, label: 'H11' },
    { element: 'H', fx: 0.614, fy: 0.114, fz: 0.886, label: 'H12' },
    { element: 'H', fx: 0.886, fy: 0.886, fz: 0.386, label: 'H13' },
    { element: 'H', fx: 0.886, fy: 0.614, fz: 0.114, label: 'H14' },
    { element: 'H', fx: 0.614, fy: 0.886, fz: 0.114, label: 'H15' },
    { element: 'H', fx: 0.614, fy: 0.614, fz: 0.386, label: 'H16' },
  ],
  molecular: true,
};

// Carbon Disulfide (CS₂) — Pa-3 with 4 linear S=C=S molecules along the
// four [111] body diagonals (same structure type as dry ice). C-S = 1.56
// Å, a = 6.8 Å. Real low-T solid CS₂ is orthorhombic Cmca; this is a
// pedagogical Pa-3 representation.
export const SOLID_CS2: CrystalState = {
  name: 'Carbon Disulfide',
  lattice: { a: 6.8, b: 6.8, c: 6.8, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P a -3',
  basis: [
    { element: 'C', fx: 0.25, fy: 0.25, fz: 0.25, label: 'C1' },
    { element: 'C', fx: 0.25, fy: 0.75, fz: 0.75, label: 'C2' },
    { element: 'C', fx: 0.75, fy: 0.25, fz: 0.75, label: 'C3' },
    { element: 'C', fx: 0.75, fy: 0.75, fz: 0.25, label: 'C4' },
    // mol1 axis (1,1,1)
    { element: 'S', fx: 0.3825, fy: 0.3825, fz: 0.3825, label: 'S1' },
    { element: 'S', fx: 0.1175, fy: 0.1175, fz: 0.1175, label: 'S2' },
    // mol2 axis (-1,-1,1)
    { element: 'S', fx: 0.1175, fy: 0.6175, fz: 0.8825, label: 'S3' },
    { element: 'S', fx: 0.3825, fy: 0.8825, fz: 0.6175, label: 'S4' },
    // mol3 axis (-1,1,-1)
    { element: 'S', fx: 0.6175, fy: 0.8825, fz: 0.1175, label: 'S5' },
    { element: 'S', fx: 0.8825, fy: 0.6175, fz: 0.3825, label: 'S6' },
    // mol4 axis (1,-1,-1)
    { element: 'S', fx: 0.8825, fy: 0.1175, fz: 0.6175, label: 'S7' },
    { element: 'S', fx: 0.6175, fy: 0.3825, fz: 0.8825, label: 'S8' },
  ],
  molecular: true,
};

// Arsine (AsH₃) — phosphine analog with As. Pyramidal AsH₃ molecules at
// shifted FCC sites, lone pair along +[111]. As-H = 1.52 Å, a = 6.0 Å.
export const SOLID_ARSINE: CrystalState = {
  name: 'Arsine',
  lattice: { a: 6.0, b: 6.0, c: 6.0, alpha: 90, beta: 90, gamma: 90 },
  spaceGroup: 'P 1',
  basis: [
    { element: 'As', fx: 0.25, fy: 0.25, fz: 0.25, label: 'As1' },
    { element: 'As', fx: 0.75, fy: 0.25, fz: 0.75, label: 'As2' },
    { element: 'As', fx: 0.25, fy: 0.75, fz: 0.75, label: 'As3' },
    { element: 'As', fx: 0.75, fy: 0.75, fz: 0.25, label: 'As4' },
    // 3 H around As1 (lone pair along +[111])
    { element: 'H', fx: 0.396, fy: 0.104, fz: 0.104, label: 'H1' },
    { element: 'H', fx: 0.104, fy: 0.396, fz: 0.104, label: 'H2' },
    { element: 'H', fx: 0.104, fy: 0.104, fz: 0.396, label: 'H3' },
    // 3 H around As2
    { element: 'H', fx: 0.896, fy: 0.104, fz: 0.604, label: 'H4' },
    { element: 'H', fx: 0.604, fy: 0.396, fz: 0.604, label: 'H5' },
    { element: 'H', fx: 0.604, fy: 0.104, fz: 0.896, label: 'H6' },
    // 3 H around As3
    { element: 'H', fx: 0.396, fy: 0.604, fz: 0.604, label: 'H7' },
    { element: 'H', fx: 0.104, fy: 0.896, fz: 0.604, label: 'H8' },
    { element: 'H', fx: 0.104, fy: 0.604, fz: 0.896, label: 'H9' },
    // 3 H around As4
    { element: 'H', fx: 0.896, fy: 0.604, fz: 0.104, label: 'H10' },
    { element: 'H', fx: 0.604, fy: 0.896, fz: 0.104, label: 'H11' },
    { element: 'H', fx: 0.604, fy: 0.604, fz: 0.396, label: 'H12' },
  ],
  molecular: true,
};

// Bonding category — drives the grouped import dropdown ("Ionic crystals",
// "Covalent crystals", "Metallic crystals", "Molecular crystals"). For a
// few borderline cases the heuristic flags+charges-presence rule gets the
// wrong answer (Pyrite: ionic framework with S-S covalent bonds; the
// polar wurtzites GaN / AlN / SiC have no charges set but get filed as
// covalent here, matching the bond-detection treatment in elementVisuals).
export type CrystalCategory = 'metallic' | 'ionic' | 'covalent' | 'molecular';

export const crystalCategory = (c: CrystalState): CrystalCategory => {
  if (c.molecular) return 'molecular';
  if (c.metallic) return 'metallic';
  if (c.elementCharges && Object.keys(c.elementCharges).length > 0) return 'ionic';
  return 'covalent';
};

export const ALL_PRESETS: CrystalState[] = [
  // Ionic rocksalt / fluorite / CsCl-type / perovskite / rutile
  NACL,
  NAF_ROCKSALT,
  NAI_ROCKSALT,
  KBR_ROCKSALT,
  LITHIUM_FLUORIDE,
  POTASSIUM_CHLORIDE,
  MGO_ROCKSALT,
  CAO_ROCKSALT,
  SRO_ROCKSALT,
  LEAD_SULFIDE,
  CSCL,
  CESIUM_IODIDE,
  FLUORITE,
  SRF2_FLUORITE,
  BAF2_FLUORITE,
  LI2O_ANTIFLUORITE,
  CATIO3_PEROVSKITE,
  BATIO3_PEROVSKITE,
  PBTIO3_PEROVSKITE,
  MGF2_RUTILE,
  SNO2_RUTILE,
  MNO_ROCKSALT,
  NIO_ROCKSALT,
  // Covalent diamond / zincblende / wurtzite
  DIAMOND,
  LONSDALEITE,
  SILICON,
  GERMANIUM,
  ALPHA_TIN,
  GAAS_ZINCBLENDE,
  GAP_ZINCBLENDE,
  GASB_ZINCBLENDE,
  INP_ZINCBLENDE,
  INAS_ZINCBLENDE,
  INSB_ZINCBLENDE,
  ALP_ZINCBLENDE,
  ALAS_ZINCBLENDE,
  ALSB_ZINCBLENDE,
  BP_ZINCBLENDE,
  BAS_ZINCBLENDE,
  CDTE_ZINCBLENDE,
  ZINC_SULFIDE,
  ZNS_WURTZITE,
  ZNSE_ZINCBLENDE,
  ZNTE_ZINCBLENDE,
  CINNABAR,
  METACINNABAR,
  COLORADOITE,
  BEO_WURTZITE,
  SILICON_CARBIDE,
  CBN_CUBIC,
  PYRITE_FES2,
  GALLIUM_NITRIDE,
  ALUMINUM_NITRIDE,
  BETA_CRISTOBALITE,
  SODALITE,
  ZEOLITE_A,
  CHABAZITE,
  MORDENITE,
  FAUJASITE,
  SILICALITE,
  // Metallic FCC / BCC / HCP
  ALUMINUM_FCC,
  COPPER_FCC,
  NICKEL_FCC,
  SILVER_FCC,
  GOLD_FCC,
  PLATINUM_FCC,
  IRON_BCC,
  TUNGSTEN_BCC,
  LITHIUM_BCC,
  SODIUM_BCC,
  VANADIUM_BCC,
  CHROMIUM_BCC,
  TANTALUM_BCC,
  MOLYBDENUM_BCC,
  MAGNESIUM_HCP,
  ZINC_HCP,
  TITANIUM_HCP,
  COBALT_HCP,
  BERYLLIUM_HCP,
  ZIRCONIUM_HCP,
  PALLADIUM_FCC,
  // Ordered intermetallics
  CU3AU_L12,
  AU3CU_L12,
  CUAU_L10,
  BETA_BRASS,
  NIAL_B2,
  NI3AL_L12,
  NIAL3_D020,
  // Hexagonal layered + complex oxides (mixed/ionic)
  GRAPHITE,
  GRAPHENE,
  BILAYER_GRAPHENE,
  MOS2_MONOLAYER,
  MOS2_BULK_2H,
  HBN_HEXAGONAL,
  ZNO_WURTZITE,
  CDS_WURTZITE,
  RUTILE_TIO2,
  ANATASE_TIO2,
  SRTIO3_PEROVSKITE,
  // Molecular / van der Waals
  DRY_ICE_CO2,
  NITROGEN_N2,
  ICE_IH,
  SOLID_ARGON,
  SOLID_METHANE,
  SOLID_HYDROGEN,
  SOLID_CO,
  SOLID_KRYPTON,
  SOLID_XENON,
  SOLID_NEON,
  SOLID_AMMONIA,
  SOLID_SF6,
  SOLID_ACETYLENE,
  SOLID_CF4,
  SOLID_CCL4,
  SOLID_N2O,
  SOLID_HCN,
  WHITE_PHOSPHORUS,
  SOLID_CYANOGEN,
  SOLID_SILANE,
  SOLID_PHOSPHINE,
  SOLID_CBR4,
  SOLID_GERMANE,
  SOLID_CS2,
  SOLID_ARSINE,
];
